import { createContext, useContext, useEffect, useState } from 'react';
import { isNativeApp } from '../utils/platform.js';

export const DEFAULT_LANG = 'te';
export const LANG_PICKED_KEY = 'rk_lang_picked';
export const SUPPORTED_LANGS = ['en', 'te', 'ta', 'kn'];
export const LANG_OPTIONS = [
  { code: 'te', label: 'తెలుగు', short: 'తె' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ta', label: 'தமிழ்', short: 'த' },
  { code: 'kn', label: 'ಕನ್ನಡ', short: 'ಕ' },
];

export function hasLangPicked() {
  try {
    if (localStorage.getItem(LANG_PICKED_KEY)) return true;
    if (localStorage.getItem('rk_lang')) {
      markLangPicked();
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

export function markLangPicked() {
  try {
    localStorage.setItem(LANG_PICKED_KEY, '1');
  } catch {
    /* ignore */
  }
}

function detectBrowserLang() {
  if (typeof navigator === 'undefined') return DEFAULT_LANG;
  const candidates = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];
  for (const raw of candidates) {
    if (!raw) continue;
    const code = raw.split('-')[0].toLowerCase();
    if (SUPPORTED_LANGS.includes(code)) return code;
  }
  return DEFAULT_LANG;
}

function readStoredLang() {
  try {
    const stored = localStorage.getItem('rk_lang');
    const picked = !!localStorage.getItem(LANG_PICKED_KEY);
    if (isNativeApp()) {
      if (picked && stored && SUPPORTED_LANGS.includes(stored)) return stored;
      return 'en';
    }
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  } catch {
    /* ignore */
  }
  return isNativeApp() ? 'en' : detectBrowserLang();
}

export const translations = {
  te: {
    location: '📍 అమరావతి, ఆంధ్రప్రదేశ్',
    logout: 'లాగ్ అవుట్',
    signInCopy: 'ఒక Google ఖాతా — సైన్ ఇన్ తర్వాత e-paper, పోల్, వార్తలు చదవండి. సభ్యత్వంతో పూర్తి యాక్సెస్.',
    signInLoading: 'సైన్ ఇన్ అవుతోంది…',
    signInBtn: 'Google తో కొనసాగించండి',
    signInEdition: 'డిజిటల్ సంచిక · 2026',
    signInLabel: 'సభ్య సైన్-ఇన్',
    signInHeadline: 'వార్తా',
    signInHeadlineEm: 'గదిలోకి',
    signInMetaPerYear: 'సంవత్సరానికి',
    signInMetaEpaper: 'E-paper',
    signInMetaEpaperSub: 'రోజువారీ + ఆర్కైవ్',
    signInMetaSecure: 'సురక్షితం',
    signInMetaSecureSub: 'Google మాత్రమే',
    signInLegal: 'సైన్ ఇన్ చేయడం ద్వారా మా నిబంధనలను అంగీకరిస్తారు. Google నుండి మీ పేరు మరియు ఇమెయిల్ మాత్రమే నిల్వ చేస్తాము.',
    signInTickerTruth: 'నిజాయితీ పత్రికాశాఖ',
    signInTickerEpaper: 'తెలుగు e-paper',
    signInTickerEditions: 'రోజువారీ పత్రిక & నెలవారీ మ్యాగజైన్',
    paperSliderAria: 'పేజీ స్లైడర్',
    paperPagesAria: 'పేజీలు',
    signInErrorApiDev: 'API నడవడం లేదు. మరో టెర్మినల్‌లో: npm run server',
    signInErrorCancelled: 'Google సైన్ ఇన్ రద్దు అయింది లేదా నిరోధించబడింది.',
    signInErrorNetwork: 'సర్వర్‌ను చేరుకోలేకపోయాం. కనెక్షన్ చూసి మళ్లీ ప్రయత్నించండి.',
    signInErrorFailed: 'Google సైన్ ఇన్ విఫలమైంది. మళ్లీ ప్రయత్నించండి.',
    login: 'లాగిన్',
    launchBadge: '5 ఆగస్టు ప్రారంభం',
    launchLiveBadge: 'లైవ్',
    launchTitle: '5 ఆగస్టు 2026 — రేపటికోసం అధికారిక వెబ్‌సైట్ ప్రారంభం',
    launchSub: 'నిజాయితీగా · నిర్భయంగా · నిస్వార్థంగా',
    launchLiveTitle: 'రేపటికోసం అధికారికంగా ప్రారంభమైంది!',
    launchLiveSub: 'రేపటికోసం అధికారిక వెబ్‌సైట్‌కు స్వాగతం',
    launchCta: 'పత్రిక చూడండి',
    launchDays: 'రోజులు',
    launchHours: 'గంటలు',
    launchMins: 'నిమి',
    launchSecs: 'సెకన్లు',
    launchDismiss: 'మూసివేయి',
    marketingBadge: 'వార్షిక పాస్',
    marketingPromoText: 'రోజువారీ e-paper + ఆర్కైవ్ — ₹999/సంవత్సరం (GST అదనం)',
    marketingSubscribeBtn: 'ఇప్పుడే సబ్‌స్క్రైబ్ చేయండి',
    marketingShareBtn: 'WhatsApp లో షేర్ చేయండి',
    marketingShareText: 'రేపటికోసం తెలుగు దినపత్రిక — నిజాయితీ వార్తలు, రోజువారీ e-paper',
    marketingSectionEyebrow: 'డిజిటల్ సభ్యత్వం',
    marketingSectionTitle: 'మొత్తం ఎడిషన్ అన్లాక్ చేయండి',
    marketingSectionLead: 'పత్రికలు, పోల్స్, ప్రత్యేక కథనాలు — మీ Google ఖాతాతో ఒక సంవత్సరం పూర్తి యాక్సెస్.',
    marketingPerk1: 'రోజువారీ e-paper & నెలవారీ మ్యాగజైన్',
    marketingPerk2: 'గత సంచికల ఆర్కైవ్',
    marketingPerk3: 'పోల్స్ & ప్రత్యేక కథనాలు',
    marketingPerk4: 'అన్ని పరికరాల్లో చదవండి',
    marketingPerYear: 'సంవత్సరం',
    marketingGstNote: '+ 18% GST · Razorpay సురక్షిత చెల్లింపు',
    marketingSecureNote: 'చెల్లింపు తర్వాత తక్షణ యాక్సెస్',
    marketingFloatSubscribe: '₹999 — సబ్‌స్క్రైబ్',
    sidebarSubscribeLead: 'పూర్తి రోజువారీ e-paper + ఆర్కైవ్ చదవండి.',
    previewBtn: 'మొదటి 2 పేజీలు ఉచితం',
    previewEyebrow: 'ఉచితం',
    previewTitle: 'మొదటి 2 పేజీలు చదవండి',
    previewSub: 'చందా అవసరం లేదు — ఇప్పుడే పత్రికను చూడండి',
    previewBtnAction: 'ఉచితంగా ప్రారంభించండి',
    previewLimitBanner: 'ప్రివ్యూ ముగిసింది — పూర్తి ఎడిషన్ కోసం సబ్‌స్క్రైబ్ చేయండి',
    previewSubscribeCta: '₹999 — సబ్‌స్క్రైబ్',
    socialProofTitle: 'పాఠకులు మాపై నమ్మకం',
    socialProofReaders: 'పాఠకులు',
    socialProofSubs: 'సభ్యులు',
    socialProofLeads: 'సాంపిల్ డౌన్‌లోడ్',
    testimonialsEyebrow: 'పాఠకుల అభిప్రాయాలు',
    testimonialsTitle: 'పాఠకులు రేపటికోసం గురించి ఏమంటారు',
    testimonialsSub: 'తెలుగు వార్తలు, రోజువారీ పత్రిక — నమ్మకమైన చదువుదారులు',
    testimonial1: 'నిజాయితీ వార్తలు, రోజువారీ e-paper — ప్రతి ఉదయం విజయవాడ నుండి చదువుతున్నాను.',
    testimonial1Author: 'రామారావు',
    testimonial1Location: 'విజయవాడ, ఆంధ్రప్రదేశ్',
    testimonial2: 'మాసిక మ్యాగజైన్ + పాత సంచికలు ఒకే చోట — చాలా సౌకర్యం.',
    testimonial2Author: 'లక్ష్మీ',
    testimonial2Location: 'హైదరాబాద్, తెలంగాణ',
    testimonial3: 'ప్రతి ఉదయం రేపటికోసం తో ప్రారంభిస్తాను — నమ్మకమైన, నిష్పక్షపాత వార్తలు.',
    testimonial3Author: 'సురేష్',
    testimonial3Location: 'గుంటూరు, ఆంధ్రప్రదేశ్',
    testimonial4: 'ఆర్కైవ్ సంచికలు ఒకే చోట — డిజిటల్ పత్రికలో అత్యుత్తమ అనుభవం.',
    testimonial4Author: 'ప్రియా',
    testimonial4Location: 'విశాఖపట్నం, ఆంధ్రప్రదేశ్',
    leadTitle: 'ఉచిత సాంపిల్ PDF',
    leadLead: 'మీ ఇమెయిల్ ఇవ్వండి — రేపటికోసం సాంపిల్ బుక్‌లెట్ డౌన్‌లోడ్ చేసుకోండి.',
    leadEmailPlaceholder: 'you@email.com',
    leadSubmit: 'డౌన్‌లోడ్ పొందండి',
    leadSuccess: 'ధన్యవాదాలు! PDF కొత్త ట్యాబ్‌లో తెరుచుకుంటుంది.',
    leadError: 'దయచేసి సరైన ఇమెయిల్ ఇవ్వండి.',
    faqTitle: 'తరచుగా అడిగే ప్రశ్నలు',
    faqSubtitle: 'సభ్యత్వం, చెల్లింపు మరియు e-paper గురించి సమాధానాలు',
    trustRibbonEyebrow: 'మీ విశ్వసనీయ తెలుగు వార్తాస్రోత',
    trustRibbonAria: 'రేపటికోసం ప్రచురణ విశ్వాసం',
    trustSince: '2015 నుండి',
    trustSinceSub: 'నమ్మకమైన ప్రచురణ',
    trustReaders: '50K+ పాఠకులు',
    trustReadersSub: 'ఆంధ్రప్రదేశ్',
    trustDaily: 'రోజువారీ e-paper',
    trustDailySub: 'ప్రతి ఉదయం',
    trustLocation: 'అమరావతి · ఆంధ్రప్రదేశ్',
    todayHeadlinesTitle: 'ఈరోజు ముఖ్యాంశాలు',
    editorsPickTitle: 'ఎడిటర్ ఎంపిక',
    editorsPickBadge: 'ఎడిటర్ ఎంపిక',
    searchEyebrow: 'త్వరిత శోధన',
    searchTitle: 'వార్తలు & పత్రికలు వెతకండి',
    searchHowItWorks: 'శీర్షిక లేదా విభాగం టైప్ చేయండి — వార్తలు మరియు పాత పత్రిక తేదీలు కనిపిస్తాయి',
    searchMinHint: 'కనీసం 2 అక్షరాలు',
    searchClear: 'తొలగించు',
    searchResultsLabel: 'శోధన ఫలితాలు',
    searchEditions: 'పత్రిక సంచికలు',
    searchStories: 'వార్తలు',
    searchNoResults: 'ఫలితాలు లేవు — మరొక పదం ప్రయత్నించండి',
    guestBarTitle: 'సైన్ ఇన్',
    guestBarText: 'ఉచితంగా 2 పేజీల ప్రివ్యూ + 1 వార్త చదవండి. పూర్తి పత్రికకు సైన్ ఇన్ చేయండి.',
    guestBarSub: 'ఉచితంగా ప్రివ్యూ చూడండి — పూర్తి పత్రికకు సైన్ ఇన్',
    guestBarPerkPages: 'ఉచిత e-paper ప్రివ్యూ పేజీలు',
    guestBarPerkStory: 'ఉచిత వార్త చదవండి',
    guestBarPerkFull: 'సైన్ ఇన్ తర్వాత పూర్తి పత్రిక',
    guestBarLater: 'తర్వాత చూస్తాను',
    guestBarBtn: 'Google తో సైన్ ఇన్',
    headerSignIn: 'సైన్ ఇన్',
    textSizeLabel: 'అక్షర పరిమాణం',
    textSize_sm: 'A',
    textSize_md: 'A+',
    textSize_lg: 'A++',
    searchPlaceholder: 'శీర్షిక, విభాగం లో శోధించండి…',
    editionLiveTitle: 'నేటి సంచిక ఇప్పుడే లైవ్!',
    editionLiveCta: 'పత్రిక చదవండి',
    shareStoryBtn: 'WhatsApp లో షేర్',
    editionToastTitle: 'నేటి సంచిక లైవ్!',
    editionToastBody: 'ఈ-పేపర్‌ను ఇప్పుడే చదవండి.',
    editionToastRead: 'పత్రిక తెరవండి',
    storyUsefulPrompt: 'ఈ కథనం ఉపయోగకరమా?',
    storyUsefulYes: 'అవును',
    storyUsefulThanks: 'ధన్యవాదాలు!',
    relatedStoriesTitle: 'సంబంధిత కథనాలు',
    freeReadBadge: 'ఉచితం',
    subscribersOnlyBadge: 'సభ్యులు',
    categoryNewsTitle: 'విభాగాల వారీగా',
    categoryViewAll: 'అన్ని చూడండి',
    whatsappChannel: 'WhatsApp ఛానెల్',
    youtubeWatchNote: 'వీడియోలు + రోజువారీ వార్తలు ఒకే చోట',
    faq1q: 'చందా ఎంత? ఏమి లభిస్తుంది?',
    faq1a: '₹999 + 18% GST = సుమారు ₹1,179/సంవత్సరం. రోజువారీ e-paper, నెలవారీ మ్యాగజైన్, ఆర్కైవ్, పోల్స్, ప్రత్యేక కథనాలు.',
    faq2q: 'చెల్లింపు తర్వాత ఎప్పుడు యాక్సెస్?',
    faq2a: 'Razorpay విజయవంతమైన వెంటనే — అదే Google ఖాతాతో లాగిన్ చేసి చదవండి.',
    faq3q: 'ఏ పరికరాల్లో చదవగలను?',
    faq3a: 'ఫోన్, టాబ్లెట్, కంప్యూటర్ — బ్రౌజర్ లేదా హోమ్ స్క్రీన్‌కు యాప్‌లా జోడించండి.',
    faq4q: 'రిఫరల్ కోడ్ ఎలా పని చేస్తుంది?',
    faq4a: 'స్నేహితుడి లింక్‌తో సైన్ అప్ — 2 రోజుల ఉచిత ట్రయల్. మీ కోడ్‌ను WhatsApp లో షేర్ చేయండి.',
    faq5q: 'రీఫండ్ ఉందా?',
    faq5a: 'డిజిటల్ సభ్యత్వం తర్వాత రీఫండ్ సాధారణంగా లేదు. సమస్యకు contact@repatikosam.com',
    faq6q: 'భాష ఎలా మార్చాలి?',
    faq6a: 'పైన కుడివైపు 🌐 భాష బటన్ నొక్కండి — తెలుగు, English, தமிழ், ಕನ್ನಡ ఎంచుకోండి. ఎప్పుడైనా మార్చవచ్చు.',
    faq7q: 'నేటి పత్రిక ఎక్కడ ఉంది?',
    faq7a: 'క్రింది బార్‌లో "ఈ పత్రిక" నొక్కండి లేదా పేజీ మధ్యలో ఉన్న పత్రిక ప్రివ్యూ నొక్కండి. లైవ్ అయితే వెంటనే చదవవచ్చు.',
    faq8q: 'అక్షరాలు పెద్దగా చూడాలంటే?',
    faq8a: 'పైన + బటన్ నొక్కండి — అక్షరాలు పెద్దగా మారుతాయి. మళ్ళీ చిన్నది కావాలంటే − నొక్కండి.',
    faq9q: 'సహాయం ఎలా పొందాలి?',
    faq9a: 'క్రింద WhatsApp బటన్ నొక్కండి — సిద్ధంగా ఉన్న సందేశాలు లేదా మా టీమ్‌కు మాట్లాడండి. ఫోన్: +91 90904 48890',
    langPickerTitle: 'మీ భాష ఎంచుకోండి',
    langPickerSubtitle: 'Choose your language',
    langPickerHint: 'తర్వాత పైన భాష బటన్ నుండి మార్చవచ్చు',
    helpGuideTitle: 'సైట్ ఎలా ఉపయోగించాలి',
    helpGuideSubtitle: '5 సులభ స్టెప్‌లు — ఒక్కసారి చదివితే సరిపోతుంది',
    helpGuideOpenFaq: 'తరచుగా అడిగే ప్రశ్నలు చూడండి',
    helpGuideCallWa: 'WhatsApp లో సహాయం',
    helpStep1Title: 'భాష మార్చండి',
    helpStep1Body: 'పైన కుడివైపు 🌐 భాష నొక్కి మీ భాష ఎంచుకోండి.',
    helpStep2Title: 'నేటి పత్రిక చదవండి',
    helpStep2Body: 'క్రింద "ఈ పత్రిక" నొక్కండి — రోజువారీ e-paper తెరుచుకుంటుంది.',
    helpStep3Title: 'అక్షరం పెద్దది చేయండి',
    helpStep3Body: 'పైన + బటన్ నొక్కండి — అక్షరాలు పెద్దగా చూడవచ్చు.',
    helpStep4Title: 'సైన్ ఇన్ / సభ్యత్వం',
    helpStep4Body: 'పూర్తి పత్రికకు పైన "సైన్ ఇన్" — Google తో లాగిన్, ₹999/సంవత్సరం.',
    helpStep5Title: 'సహాయం కావాలంటే',
    helpStep5Body: 'క్రింద WhatsApp నొక్కండి — మా టీమ్ సహాయం చేస్తుంది.',
    easyReadingLabel: 'సులభ చదవడం',
    easyReadingOn: 'ఆన్',
    easyReadingOff: 'ఆఫ్',
    navHelp: 'సహాయం',
    navStories: 'కథనాలు',
    nativeYou: 'మీరు',
    nativeToday: 'నేడు',
    nativeGuest: 'అతిథి',
    nativeGuestHint: 'ప్రివ్యూ చూడండి — పూర్తి పత్రికకు సైన్ ఇన్',
    nativeMember: 'సభ్యుడు',
    nativePrefs: 'ప్రాధాన్యతలు',
    nativeSupport: 'సహాయం',
    nativeLegal: 'చట్టపరమైన',
    nativeShareApp: 'యాప్ షేర్ చేయండి',
    nativeShare: 'షేర్',
    nativeEditionAlerts: 'కొత్త ఎడిషన్ అలర్ట్',
    nativeOfflineHint: 'ఆఫ్‌లైన్‌లో చదవండి',
    nativeBookmarks: 'సేవ్ చేసినవి',
    nativeBookmark: 'సేవ్',
    nativeBookmarkSaved: 'సేవ్ అయింది',
    nativeSearch: 'వెతకండి',
    nativeSearchPlaceholder: 'వార్తలు లేదా తేదీ…',
    nativeSearchHint: 'కనీసం 2 అక్షరాలు టైప్ చేయండి',
    nativeSearchEmpty: 'ఫలితాలు లేవు',
    nativeTheme: 'థీమ్',
    nativeThemeSystem: 'సిస్టమ్',
    nativeThemeLight: 'లైట్',
    nativeThemeDark: 'డార్క్',
    nativePassActive: 'యాక్టివ్',
    nativePassGet: 'Get',
    nativeUnlockHint: 'రోజువారీ e-paper, ఆర్కైవ్, కథనాలు అన్‌లాక్ చేయండి',
    nativeDone: 'పూర్తి',
    nativeMarketsTitle: 'మార్కెట్ రేట్లు',
    nativeMarketsToday: 'ఈరోజు',
    nativeMarketsLive: 'నేటి రేటు',
    nativeMarketGold: 'బంగారం',
    nativeMarketSilver: 'వెండి',
    nativeMarketPetrol: 'పెట్రోల్',
    nativeMarketDiesel: 'డీజిల్',
    nativeMarketTG: 'తెలంగాణ',
    nativeMarketAP: 'ఆంధ్ర',
    nativeBackToMenu: 'మెనుకు తిరిగి',
    nativeBackToApp: 'యాప్‌కు తిరిగి',
    nativeValidUntil: 'చెల్లుబాటు',
    nativeTrialUntil: 'ట్రయల్ వరకు',
    toiTopNews: 'టాప్ న్యూస్',
    navEpaper: 'ఈ-పేపర్',
    navVideos: 'వీడియోలు',
    navMenu: 'మెను',
    textSize_sm_hint: 'చిన్న',
    textSize_md_hint: 'మధ్య',
    textSize_lg_hint: 'పెద్ద',
    referralTitle: 'స్నేహితులను ఆహ్వానించండి',
    referralFabLabel: 'ఆహ్వానించండి',
    referralLead: 'మీ లింక్‌ను షేర్ చేయండి — స్నేహితుడు 2 రోజుల ఉచిత ట్రయల్ పొందుతాడు.',
    referralCopy: 'లింక్ కాపీ',
    referralCopied: 'కాపీ అయింది!',
    referralClaimPlaceholder: 'స్నేహితి కోడ్',
    referralClaimBtn: 'ట్రయల్ యాక్టివేట్',
    referralClaimSuccess: '2 రోజుల ట్రయల్ సక్రియమైంది!',
    referralClaimFail: 'కోడ్ సరిచూడండి లేదా ఇప్పటికే ఉపయోగించారు.',
    referralShareWa: 'WhatsApp షేర్',
    referralShareText: 'రేపటికోసం — తెలుగు దినపత్రిక',
    referralShareCode: 'రిఫరల్ కోడ్',
    referralTrialActive: 'మీ ట్రయల్ {date} వరకు చెల్లుతుంది',
    installIosHint: 'Share → Add to Home Screen — రోజూ ఒక ట్యాప్‌తో చదవండి',
    installIosShort: 'Add to Home Screen',
    installDesktopShort: 'డెస్క్‌టాప్ యాప్ ఇన్‌స్టాల్',
    installSideTab: 'ఇన్‌స్టాల్',
    installDesktopBtn: 'డెస్క్‌టాప్ యాప్ ఇన్‌స్టాల్',
    tickerLabel: '🔴 తాజా',
    headlines: [
      'ఆంధ్రప్రదేశ్ సీఎం చంద్రబాబు నాయుడు కొత్త అభివృద్ధి పథకాలు',
      'చంద్రబాబు నాయుడు అభివృద్ధి చేసిన రోడ్లు & హైవేలు',
      '2024 తర్వాత భూమి ధరలు భారీగా పెరిగాయి',
      'ఆంధ్రప్రదేశ్ రాజకీయాల్లో కొత్త మలుపు',
      'రేపటికోసం దినపత్రిక - నేటి ముఖ్యాంశాలు',
    ],
    todayPaper: '📰 నేటి పత్రిక',
    paperHeadline: 'రోజువారీ వార్తాపత్రిక',
    paperSub: 'తెలుగు జాతి భవిష్యత్ కోసం నిజాయితీగా...',
    readBtn: 'పత్రిక చదవండి',
    readDailyBtn: 'నేటి పత్రిక చదవండి',
    readMonthlyBtn: 'ఈ నెల మ్యాగజైన్ చదవండి',
    browsePastDaily: 'పాత రోజుల పత్రికలు',
    browsePastMonthly: 'పాత నెలల మ్యాగజైన్లు',
    pastIssues: 'పాత రోజుల పత్రికలు',
    monthlyCalSub: 'ఏ నెల మ్యాగజైన్ కావాలో ఎంచుకోండి',
    dailyEdition: 'రోజువారీ పత్రిక',
    monthlyEdition: 'నెలవారీ మ్యాగజైన్లు',
    thisMonth: '📖 ఈ నెల మ్యాగజైన్',
    monthlyHeadline: 'నెలవారీ మ్యాగజైన్లు',
    categories: [
      { label: 'జాతీయ / అంతర్జాతీయ', icon: '🌐' },
      { label: 'క్రీడలు', icon: '🏏' },
      { label: 'వినోదం', icon: '🎬' },
    ],
    viewBtn: 'చూడండి',
    videosTitle: 'రేపటికోసం వీడియోలు',
    shortsTitle: 'రేపటికోసం షార్ట్స్',
    viewAll: 'అన్నీ చూడండి →',
    aboutTitle: 'రేపటికోసం గురించి',
    aboutText: "వర్తమాన రాజకీయాలు కలుషితమై తెలుగుజాతి భవిష్యత్ కత్తిమొనపై వేలాడుతోంది. రాజకీయ విషక్రీడలో మన భావితరాలు సమిధలు కాకుండా చూడాల్సిన బాధ్యత ప్రతి ఒక్కరిపైనా వున్నది. అందుకనుగుణంగా ప్రజలను చైతన్యవంతులను చేయాలి. రాజకీయ రంగుటద్దాల నుంచి కాకుండా వాస్తవిక దృక్పథంతో ప్రజానీకానికి అవగాహన కల్పించే క్రమంలో మా వంతు బాధ్యతగా 'రేపటికోసం' దినపత్రిక, మాసిక మ్యాగజైన్ లను ప్రారంభించటం జరిగింది.",
    adsTitle: 'ప్రకటనలు',
    adSponsored: 'ప్రాయోజితం',
    adPartnerStrip: 'మా భాగస్వాములు',
    home: 'హోమ్',
    about: 'మా గురించి',
    contact: 'సంప్రదించండి',
    rights: 'All rights reserved.',
    trustStrip: 'ప్రజా ప్రయోజన దృక్పథంతో • నిజ నిర్థారణతో • బాధ్యతాయుత సంపాదకీయం',
    footerMadeInAp: 'అమరావతి, ఆంధ్రప్రదేశ్ నుండి సంపాదకీయ డిజిటల్ వేదిక.',
    nav: ['హోమ్', 'ఈ పత్రిక', 'పాత పత్రికలు', 'అభిప్రాయం'],
    navMore: 'మరిన్ని',
    navWhatsApp: 'WhatsApp',
    calTitle: 'పాత రోజుల పత్రికలు',
    calSub: 'ఏ రోజు పత్రిక కావాలో ఎంచుకోండి',
    calRecent: 'ఇటీవలి పత్రికలు',
    calAvailable: 'పత్రిక అందుబాటులో ఉంది',
    calToday: 'నేడు',
    months: ['జనవరి','ఫిబ్రవరి','మార్చి','ఏప్రిల్','మే','జూన్','జులై','ఆగస్టు','సెప్టెంబర్','అక్టోబర్','నవంబర్','డిసెంబర్'],
    archiveTitle: 'E-పేపర్ ఆర్కైవ్',
    archiveIssues: 'సంచికలు',
    archiveEmpty: 'ఇంకా పత్రికలు అప్లోడ్ కాలేదు',
    pollTitle: 'మీ అభిప్రాయం',
    pollHeadHint: 'ఒక ఎంపిక నొక్కండి',
    pollVotes: 'ఓట్లు',
    pollThanks: '✅ మీ ఓటు నమోదైంది!',
    statEditions: 'సంచికలు',
    statEst: '2015 / 2018',
    statLang: 'తెలుగు',
    statLangLabel: 'భాష',
    statState: 'ఆంధ్రప్రదేశ్',
    statStateLabel: 'రాష్ట్రం',
    latestNews: 'తాజా వార్తలు',
    articlesTitle: 'ప్రత్యేక కథనాలు',
    readMore: 'మరింత చదవండి',
    ytDesc: 'తెలుగు జాతి భవిష్యత్ కోసం నిజాయితీగా...',
    ytSubscribe: 'Subscribe చేయండి',
    ytVideos: 'వీడియోలు',
    ytShorts: 'షార్ట్స్',
    ytFeatured: 'తాజా వీడియో',
    ytTitle: 'రేపటికోసం YouTube',
    ytNowPlaying: 'ప్లే అవుతోంది',
    ytPlaylist: 'వీడియోలు',
    zonesLabel: 'AP ప్రచురణ కేంద్రాలు',
    zonesLead: 'తీరపుర ఆంధ్రప్రదేశ్ నుంచి రాయలసీమ వరకు — ఒక పత్రిక, ఆరు ప్రచురణ కేంద్రాలు.',
    zones: [
      { icon: '🕰️', name: 'గుంటూరు' },
      { icon: '🌉', name: 'విజయవాడ' },
      { icon: '🗼', name: 'శ్రీకాకుళం' },
      { icon: '🌊', name: 'విశాఖపట్నం' },
      { icon: '🌴', name: 'రాజమహేంద్రవరం' },
      { icon: '🏰', name: 'కడప' },
    ],
    brandName: 'రేపటికోసం',
    legalEntityName: 'M S Publications',
    legalEntityLabel: 'నమోదిత వ్యాపారం',
    tradeNameLabel: 'వాణిజ్య పేరు',
    gstLabel: 'GSTIN',
    businessTypeLabel: 'వ్యాపార రకం',
    youtube: 'YouTube',
    facebook: 'Facebook',
    instagram: 'Instagram',
    mobileAppEyebrow: 'మొబైల్ యాప్',
    mobileAppTitle: 'Android & iOS — త్వరలో వస్తోంది',
    mobileAppSub: '1–2 వారాలలో Google Play మరియు App Store లో డౌన్‌లోడ్ చేసుకోండి',
    mobileAppPlay: 'Google Play',
    mobileAppIos: 'App Store',
    mobileAppSoon: 'త్వరలో',
    footerQuickLinks: 'త్వరిత లింకులు',
    footerCompany: 'సంస్థ',
    footerLegal: 'నియమాలు',
    footerContact: 'సంప్రదింపు',
    footerTerms: 'నిబంధనలు & షరతులు',
    footerPrivacy: 'గోప్యతా విధానం',
    footerRefund: 'రీఫండ్ విధానం',
    footerCancellation: 'రద్దు విధానం',
    legalBackHome: '← హోమ్',
    legalLangEn: 'English',
    legalLangTe: 'తెలుగు',
    legalTeNote: 'పూర్తి నియమాలు ఇంగ్లీష్‌లో లభిస్తాయి.',
    legalPoliciesNav: 'నీతులు & సహాయం',
    legalHeroSub: 'M S Publications నిర్వహిస్తున్న రేపటికోసం కోసం అధికారిక నీతులు.',
    contactPageHeroSub: 'సభ్యత్వం, చెల్లింపు లేదా పత్రిక సమస్యలకు మాతో సంప్రదించండి.',
    contactFormSub: 'మీ వివరాలు WhatsApp లో సిద్ధంగా ఉంటాయి — పంపే ముందు సవరించవచ్చు.',
    cookieConsentTitle: 'కుకీ అంగీకారం',
    cookieConsentText: 'మేము సైట్ మెరుగుపరచడానికి విశ్లేషణ కుకీలు ఉపయోగిస్తాము. అంగీకరించండి లేదా తిరస్కరించండి.',
    cookieConsentAccept: 'అంగీకరిస్తున్నాను',
    cookieConsentReject: 'తిరస్కరించు',
    editionNotifyPrompt: 'కొత్త ఎడిషన్ వచ్చినప్పుడు నోటిఫికేషన్ పొందాలా?',
    editionNotifyEnable: 'అనుమతించు',
    editionNotifyLater: 'తర్వాత',
    editionNotifyTitle: 'రేపటికోసం — కొత్త ఎడిషన్',
    editionNotifyBody: 'నేటి దినపత్రిక ఇప్పుడు అందుబాటులో ఉంది.',
    subscribeBillingNotice: 'బిల్లింగ్: {business} · GSTIN {gstin}',
    paymentSuccessTitle: 'చెల్లింపు విజయవంతం!',
    paymentSuccessBody: 'మీ సభ్యత్వం సక్రియం చేయబడింది. ఇప్పుడు e-paper చదవండి.',
    paymentSuccessUntil: 'మీ యాక్సెస్ {date} వరకు చెల్లుతుంది.',
    paymentSuccessReceipt: 'రసీదు లింక్ క్రింద ఉంది. SMTP సెట్ చేస్తే ఇమెయిల్ కూడా వస్తుంది.',
    paymentSuccessEmailSent: 'బిల్లింగ్ రసీదు {email} కి ఇమెయిల్ చేయబడింది.',
    paymentSuccessReceiptLink: 'బిల్లింగ్ రసీదు చూడండి / డౌన్‌లోడ్',
    paymentSuccessCta: 'పత్రిక చదవండి →',
    subProcessingTitle: 'చెల్లింపు ప్రాసెస్ అవుతోంది…',
    subProcessingText: 'మీ యాక్సెస్ అన్‌లాక్ అవుతోంది…',
    subUnlockHeadline: 'పూర్తి సంచికను అన్‌లాక్ చేయండి',
    subUnlockLead: 'మీ Google ఖాతాకు లింక్ చేసిన వార్షిక సభ్యత్వం — రోజువారీ e-paper, ఆర్కైవ్, అన్ని కథనాలు.',
    subBullet1: 'రోజువారీ డిజిటల్ పత్రిక',
    subBullet2: 'నెలవారీ మ్యాగజైన్ ఆర్కైవ్',
    subBullet3: 'అన్ని పరికరాలు, ఒక ఖాతా',
    subBullet4: '365 రోజుల యాక్సెస్',
    subModalTitle: 'కొనసాగించడానికి సభ్యత్వం',
    subModalSub: 'పూర్తి e-paper మరియు ఆర్కైవ్ అన్‌లాక్ చేయండి',
    subSignedInAs: 'సైన్ ఇన్',
    subSwitchAccount: 'వేరే ఖాతా ఉపయోగించండి',
    subAnnualPass: 'వార్షిక పాస్',
    subPerYear: '/ సంవత్సరం',
    subLinkedGmail: 'ఈ Gmailకే సభ్యత్వం లింక్ అవుతుంది.',
    subPlanPrice: 'ప్లాన్ ధర',
    subGstLabel: 'GST (18%)',
    subTotalDue: 'మొత్తం చెల్లింపు',
    subPleaseWait: 'దయచేసి వేచి ఉండండి…',
    subPayUnlock: '₹{total} చెల్లించి అన్‌లాక్ చేయండి',
    subInstantAccess: 'చెల్లింపు విజయవంతమైన తర్వాత తక్షణ యాక్సెస్',
    subWhatsappLabel: 'WhatsApp నంబర్',
    subWhatsappPlaceholder: '9876543210',
    subWhatsappOptIn: 'నేటి పత్రిక లైవ్ అయినప్పుడు WhatsApp‌లో సందేశం పంపండి',
    subWhatsappRequired: 'సరైన 10 అంకెల WhatsApp నంబర్ ఇవ్వండి',
    subWhatsappOptInRequired: 'పత్రిక లైవ్ అలర్ట్‌ల కోసం WhatsApp అనుమతి ఇవ్వండి',
    subSecuredRazorpay: 'Razorpay ద్వారా సురక్షితం · UPI, కార్డులు, నెట్‌బ్యాంకింగ్',
    subDemoMode: 'డెమో మోడ్ — నిజ చెల్లింపుల కోసం సర్వర్‌లో Razorpay కీలు జోడించండి.',
    subTestModeTitle: 'టెస్ట్ చెల్లింపు మోడ్',
    subTestModeUpiNote: 'టెస్ట్ మోడ్‌లో నిజ GPay / PhonePeతో QR స్కాన్ చేస్తే invalid అని వస్తుంది — ఇది సాధారణం.',
    subTestModeUpiItem: 'UPI: success@razorpayతో చెల్లించండి',
    subTestModeCardItem: 'కార్డ్: 4111 1111 1111 1111, భవిష్యత్ expiry, ఏ CVV',
    subTestModeFoot: 'నిజ QR చెల్లింపుల కోసం KYC తర్వాత Live కీలకు మారండి.',
    subPaymentCancelled: 'చెల్లింపు రద్దు చేయబడింది',
    subPaymentError: 'చెల్లింపు పూర్తి కాలేదు. మళ్ళీ ప్రయత్నించండి.',
    subServerError: 'సర్వర్‌ను చేరుకోలేకపోయాం. కనెక్షన్ తనిఖీ చేసి మళ్ళీ ప్రయత్నించండి.',
    subInvalidPayment: 'సర్వర్ నుండి చెల్లింపు సమాచారం సరైనది కాదు.',
    subVerifyFailed: 'చెల్లింపు ధృవీకరణ విఫలమైంది',
    subOrderError: 'చెల్లింపు ప్రారంభించలేకపోయాం. మళ్ళీ ప్రయత్నించండి.',
    feedErrorMsg: 'వార్తలు లోడ్ కాలేదు. కనెక్షన్ తనిఖీ చేయండి.',
    feedErrorRetry: 'మళ్ళీ ప్రయత్నించండి',
    footerLocation: 'అమరావతి, ఆంధ్రప్రదేశ్',
    insideLabel: 'ఈ సంచికలో',
    insidePolitics: 'రాజకీయాలు',
    insideAgri: 'వ్యవసాయం',
    insideEdu: 'విద్య',
    insideNational: 'జాతీయం',
    insideIntl: 'అంతర్జాతీయం',
    insideSports: 'క్రీడలు',
    yearsLabel: 'సంవత్సరాలు',
    issuesLabel: 'సంచికలు',
    readersLabel: 'పాఠకులు',
    loading: 'లోడ్ అవుతోంది...',
    paperNotAvailable: 'ఈ తేదీకి పత్రిక అందుబాటులో లేదు',
    serverError: 'సర్వర్ లోపం. దయచేసి మళ్ళీ ప్రయత్నించండి.',
    closeBtn: 'మూసివేయి',
    calDays: ['ఆది', 'సోమ', 'మంగళ', 'బుధ', 'గురు', 'శుక్ర', 'శని'],
    catAll: 'అన్నీ',
    catPolitics: 'రాజకీయాలు',
    catNews: 'వార్తలు',
    catTurmeric: 'పసుపు పరిమళాలు',
    listenBtn: 'వినండి',
    stopListen: 'ఆపండి',
    readAloudTitle: 'బిగ్గరగా చదవండి',
    zonesCountSuffix: 'కేంద్రాలు',
    pollLangTip: 'పోల్ ప్రశ్న తెలుగులో ఉంది',
    sharePaperBadge: 'పత్రిక',
    shareWhatsAppTitle: 'WhatsApp లో షేర్ చేయండి',
    paperViewerTitle: 'రేపటికోసం పత్రిక',
    shareMessage: 'రేపటికోసం దినపత్రిక',
    paperScrollHint: 'పేజీలు — కిందికి స్క్రోల్ చేయండి',
    paperPrev: 'మునుపటి',
    paperNext: 'తదుపరి',
    paperPageOf: 'పేజీ',
    paperViewScroll: 'స్క్రోల్',
    paperViewPages: 'పేజీలు',
    paperScrollAllHint: 'కిందికి స్క్రోల్ చేసి అన్ని పేజీలు చూడండి',
    paperZoomLabel: 'జూమ్',
    paperZoomIn: 'జూమ్ ఇన్',
    paperZoomOut: 'జూమ్ అవుట్',
    paperZoomReset: 'రీసెట్',
    paperRetry: 'మళ్ళీ ప్రయత్నించండి',
    paperSwipeHint: 'పేజీలు మార్చడానికి ఎడమ/కుడి స్వైప్ చేయండి',
    paperPinchHint: 'జూమ్ చేయడానికి రెండు వేలులతో పించ్ చేయండి · పాన్ చేయడానికి లాగండి',
    installTitle: 'రేపటికోసం',
    installSub: 'యాప్ గా ఇన్‌స్టాల్ చేయండి',
    installBtn: 'ఇన్‌స్టాల్',
    dismissBtn: 'తీసివేయి',
    aboutHeroTagline: 'తెలుగు జాతి భవిష్యత్ కోసం నిజాయితీగా...',
    aboutSectionHeading: 'మా గురించి',
    roleManagingDirector: 'మేనేజింగ్ డైరెక్టర్',
    roleChiefEditor: 'చీఫ్ ఎడిటర్',
    aboutLeadershipHeading: 'మా నాయకత్వం',
    aboutStatMonthly: 'మాసిక మ్యాగజైన్',
    aboutStatDaily: 'దినపత్రిక',
    aboutStatState: 'ఆంధ్రప్రదేశ్',
    aboutStatStateLabel: 'రాష్ట్రం',
    aboutSocialMedia: 'సోషల్ మీడియా',
    contactAddressLabel: 'చిరునామా',
    contactAddress: 'డోర్ నం. 5-82-25, 4వ లేన్, పండరిపురం, గుంటూరు, ఆంధ్రప్రదేశ్ 522006, భారతదేశం',
    contactEmailLabel: 'ఇమెయిల్',
    contactFormTitle: 'సందేశం పంపండి',
    contactNameLabel: 'పేరు',
    contactPhoneLabel: 'ఫోన్ నంబర్',
    contactWhatsAppLabel: 'WhatsApp',
    contactReachUs: 'మమ్మల్ని సంప్రదించండి',
    contactBusinessTitle: 'నమోదిత వ్యాపారం',
    contactIntlNoteLabel: 'అంతర్జాతీయ కస్టమర్లు',
    contactIntlNote:
      'రేపటికోసం Razorpay ద్వారా భారత్ మరియు అంతర్జాతీయ చెల్లింపులను అంగీకరిస్తుంది (INR). అంతర్జాతీయ కార్డ్‌లపై మీ బ్యాంక్ కరెన్సీ మార్పిడి ఫీజు విధించవచ్చు.',
    contactMessageLabel: 'సందేశం',
    contactNamePlaceholder: 'మీ పేరు',
    contactPhonePlaceholder: 'మీ ఫోన్ నంబర్',
    contactMessagePlaceholder: 'మీ సందేశం...',
    contactSentSuccess: 'మీ సందేశం WhatsApp కి పంపబడింది!',
    contactSendAgain: 'మళ్ళీ పంపండి',
    contactSendViaWa: 'WhatsApp లో పంపండి',
    contactWaIntro: 'రేపటికోసం వెబ్‌సైట్ నుండి సందేశం:',
    contactWaName: 'పేరు',
    contactWaPhone: 'ఫోన్',
    contactWaMessage: 'సందేశం',
    waSuggestTitle: 'ఏ సహాయం కావాలి?',
    waSuggestSub: 'ఒకటి ఎంచుకోండి — WhatsApp లో సందేశం సిద్ధంగా ఉంటుంది',
    waSuggestSendHint: 'సందేశం ఎంచుకున్న తర్వాత WhatsApp తెరుచుకుని Send నొక్కండి',
    waSuggestClose: 'రద్దు',
    waSuggest_subscribe: 'సభ్యత్వం ఎలా పొందాలి?',
    waSuggest_subscribe_msg:
      'నమస్కారం, రేపటికోసం సభ్యత్వం (₹999) ఎలా పొందాలో వివరాలు చెప్పండి.',
    waSuggest_payment: 'చెల్లింపు చేసాను — పత్రిక రావడం లేదు',
    waSuggest_payment_msg:
      'నమస్కారం, నేను చెల్లింపు చేసాను కానీ పూర్తి పత్రిక తెరవలేకపోతున్నాను. దయచేసి సహాయం చేయండి.',
    waSuggest_login: 'లాగిన్ / సైన్ అప్ సమస్య',
    waSuggest_login_msg:
      'నమస్కారం, Google తో లాగిన్ అవ్వలేకపోతున్నాను. దయచేసి సహాయం చేయండి.',
    waSuggest_paper: 'పత్రిక లోడ్ అవ్వడం లేదు',
    waSuggest_paper_msg:
      'నమస్కారం, ఈరోజు పత్రిక తెరవలేకపోతున్నాను. దయచేసి చూడండి.',
    waSuggest_refund: 'రీఫండ్ / బిల్ రసీదు',
    waSuggest_refund_msg:
      'నమస్కారం, రీఫండ్ లేదా పేమెంట్ రసీదు కోసం సంప్రదిస్తున్నాను.',
    waSuggest_language: 'భాష ఎలా మార్చాలి?',
    waSuggest_language_msg:
      'నమస్కారం, రేపటికోసం సైట్‌లో భాష ఎలా మార్చాలో నాకు సహాయం కావాలి.',
    waSuggest_guide: 'సైట్ ఎలా ఉపయోగించాలి?',
    waSuggest_guide_msg:
      'నమస్కారం, రేపటికోసం సైట్‌ను ఎలా ఉపయోగించాలో నాకు చెప్పగలరా?',
    waSuggest_other: 'ఇతర సహాయం',
    waSuggest_other_msg: 'నమస్కారం, రేపటికోసం గురించి నాకు సహాయం కావాలి.',
    headerTaglineFull: 'తెలుగు జాతి భవిష్యత్ కోసం నిజాయితీగా · నిర్భయంగా · నిస్వార్థంగా',
    headerEditionLive: 'లైవ్',
    headerEditionSealTop: 'రేపటికోసం',
    headerEditionSealBottom: 'దినపత్రిక',
  },
    artScrollHint: '← స్క్రోల్ →',
    noStoriesYet: 'కథనాలు త్వరలో ఇక్కడ కనిపిస్తాయి.',
    poweredBy: 'పవర్డ్ బై',
    skipToContent: 'కంటెంట్‌కు వెళ్లండి',
    loadingVideos: 'వీడియోలు లోడ్ అవుతున్నాయి…',

  en: {
    location: '📍 Amaravati, Andhra Pradesh',
    logout: 'Logout',
    signInCopy: 'Sign in with Google to read the e-paper, vote in polls, and open exclusive stories. Subscribe for full access.',
    signInLoading: 'Signing in…',
    signInBtn: 'Continue with Google',
    signInEdition: 'Digital edition · 2026',
    signInLabel: 'Member sign-in',
    signInHeadline: 'Step into the',
    signInHeadlineEm: 'newsroom',
    signInMetaPerYear: 'per year',
    signInMetaEpaper: 'E-paper',
    signInMetaEpaperSub: 'daily + archive',
    signInMetaSecure: 'Secure',
    signInMetaSecureSub: 'Google only',
    signInLegal: 'By signing in you accept our terms. We only store your name and email from Google.',
    signInTickerTruth: 'Truthful journalism',
    signInTickerEpaper: 'Telugu e-paper',
    signInTickerEditions: 'Daily paper & monthly magazine',
    paperSliderAria: 'Page slider',
    paperPagesAria: 'Pages',
    signInErrorApiDev: 'API not running. In a second terminal run: npm run server',
    signInErrorCancelled: 'Google sign-in was cancelled or blocked.',
    signInErrorNetwork: 'Cannot reach the server. Check your connection and try again.',
    signInErrorFailed: 'Google sign-in failed. Please try again.',
    login: 'Login',
    launchBadge: '5 Aug launch',
    launchLiveBadge: 'Live',
    launchTitle: '5 August 2026 — Official Repatikosam website launch',
    launchSub: 'Honestly · Fearlessly · Selflessly',
    launchLiveTitle: 'Repatikosam is officially live!',
    launchLiveSub: 'Welcome to the official Repatikosam website',
    launchCta: 'View the paper',
    launchDays: 'Days',
    launchHours: 'Hours',
    launchMins: 'Mins',
    launchSecs: 'Secs',
    launchDismiss: 'Dismiss',
    marketingBadge: 'Annual pass',
    marketingPromoText: 'Daily e-paper + archive — ₹999/year (+ GST)',
    marketingSubscribeBtn: 'Subscribe now',
    marketingShareBtn: 'Share on WhatsApp',
    marketingShareText: 'Repatikosam Telugu daily — honest news & digital e-paper',
    marketingSectionEyebrow: 'Digital membership',
    marketingSectionTitle: 'Unlock the full edition',
    marketingSectionLead: 'Papers, polls, and exclusive stories — one year of full access with your Google account.',
    marketingPerk1: 'Daily e-paper & monthly magazine',
    marketingPerk2: 'Past editions archive',
    marketingPerk3: 'Polls & exclusive stories',
    marketingPerk4: 'Read on all devices',
    marketingPerYear: 'year',
    marketingGstNote: '+ 18% GST · Secured by Razorpay',
    marketingSecureNote: 'Instant access after payment',
    marketingFloatSubscribe: '₹999 — Subscribe',
    sidebarSubscribeLead: 'Read the full daily e-paper and archive.',
    previewBtn: 'Try first 2 pages free',
    previewEyebrow: 'Free',
    previewTitle: 'Read the first 2 pages',
    previewSub: 'No subscription needed — try the paper now',
    previewBtnAction: 'Start free read',
    previewLimitBanner: 'Preview ended — subscribe for the full edition',
    previewSubscribeCta: '₹999 — Subscribe',
    socialProofTitle: 'Readers trust Repatikosam',
    socialProofReaders: 'Readers',
    socialProofSubs: 'Members',
    socialProofLeads: 'Sample downloads',
    testimonialsEyebrow: 'Reader voices',
    testimonialsTitle: 'What readers say about Repatikosam',
    testimonialsSub: 'Honest Telugu news and daily e-paper — trusted by readers across AP & Telangana',
    testimonial1: 'Honest Telugu news and daily e-paper — I read from Vijayawada every morning.',
    testimonial1Author: 'Ramarao',
    testimonial1Location: 'Vijayawada, Andhra Pradesh',
    testimonial2: 'Monthly magazine plus past editions in one place — very convenient.',
    testimonial2Author: 'Lakshmi',
    testimonial2Location: 'Hyderabad, Telangana',
    testimonial3: 'I start every morning with Repatikosam — reliable, unbiased Telugu news.',
    testimonial3Author: 'Suresh',
    testimonial3Location: 'Guntur, Andhra Pradesh',
    testimonial4: 'Best digital Telugu paper — archive access in one place is excellent.',
    testimonial4Author: 'Priya',
    testimonial4Location: 'Visakhapatnam, Andhra Pradesh',
    leadTitle: 'Free sample PDF',
    leadLead: 'Enter your email to download the Repatikosam sample booklet.',
    leadEmailPlaceholder: 'you@email.com',
    leadSubmit: 'Get download',
    leadSuccess: 'Thank you! Your PDF opens in a new tab.',
    leadError: 'Please enter a valid email.',
    faqTitle: 'Frequently asked questions',
    faqSubtitle: 'Answers about membership, payments, and the digital e-paper',
    trustRibbonEyebrow: 'Your trusted Telugu news source',
    trustRibbonAria: 'Repatikosam trust highlights',
    trustSince: 'Publishing since 2015',
    trustSinceSub: 'Trusted legacy',
    trustReaders: '50K+ readers',
    trustReadersSub: 'Andhra Pradesh',
    trustDaily: 'Daily digital e-paper',
    trustDailySub: 'Every morning',
    trustLocation: 'Amaravati · Andhra Pradesh',
    todayHeadlinesTitle: "Today's headlines",
    editorsPickTitle: "Editor's pick",
    editorsPickBadge: "Editor's pick",
    searchEyebrow: 'Quick search',
    searchTitle: 'Search news & editions',
    searchHowItWorks: 'Type a headline or category — finds stories and past edition dates',
    searchMinHint: 'Min. 2 characters',
    searchClear: 'Clear',
    searchResultsLabel: 'Search results',
    searchEditions: 'Editions',
    searchStories: 'Stories',
    searchNoResults: 'No results — try another word',
    guestBarTitle: 'Sign in',
    guestBarText: 'Read 2 free preview pages + 1 story. Sign in for the full paper.',
    guestBarSub: 'Try free preview — sign in for the full e-paper',
    guestBarPerkPages: 'Free e-paper preview pages',
    guestBarPerkStory: 'One story free to read',
    guestBarPerkFull: 'Full paper after sign-in',
    guestBarLater: 'Maybe later',
    guestBarBtn: 'Sign in with Google',
    headerSignIn: 'Sign in',
    textSizeLabel: 'Text size',
    textSize_sm: 'A',
    textSize_md: 'A+',
    textSize_lg: 'A++',
    searchPlaceholder: 'Search by headline or category…',
    editionLiveTitle: "Today's edition is live!",
    editionLiveCta: 'Read the paper',
    shareStoryBtn: 'Share on WhatsApp',
    editionToastTitle: "Today's edition is live!",
    editionToastBody: 'Read today\'s e-paper now.',
    editionToastRead: 'Open paper',
    storyUsefulPrompt: 'Was this story helpful?',
    storyUsefulYes: 'Yes',
    storyUsefulThanks: 'Thank you for your feedback!',
    relatedStoriesTitle: 'Related stories',
    freeReadBadge: 'Free',
    subscribersOnlyBadge: 'Members',
    categoryNewsTitle: 'News by category',
    categoryViewAll: 'View all',
    whatsappChannel: 'WhatsApp Channel',
    youtubeWatchNote: 'Videos + daily news in one place',
    faq1q: 'How much is the subscription?',
    faq1a: '₹999/year + 18% GST (about ₹1,179). Includes daily e-paper, monthly magazine, archive, polls, and exclusive stories.',
    faq2q: 'When do I get access after payment?',
    faq2a: 'Instantly after Razorpay success — sign in with the same Google account you used to pay.',
    faq3q: 'Which devices are supported?',
    faq3a: 'Phone, tablet, and desktop browsers. You can also add the site to your home screen like an app.',
    faq4q: 'How do referral codes work?',
    faq4a: 'Share your link — friends get a 2-day free trial when they sign up with Google.',
    faq5q: 'Is there a refund?',
    faq5a: 'Digital memberships are generally non-refundable. Contact contact@repatikosam.com for billing issues.',
    faq6q: 'How do I change the language?',
    faq6a: 'Tap 🌐 Language at the top right — choose Telugu, English, Tamil, or Kannada. You can change it anytime.',
    faq7q: "Where is today's paper?",
    faq7a: 'Tap "Today\'s paper" in the bottom bar, or the paper preview in the middle of the page.',
    faq8q: 'How do I make text bigger?',
    faq8a: 'Tap the + button at the top to make text bigger. Tap − to make it smaller again.',
    faq9q: 'How do I get help?',
    faq9a: 'Tap WhatsApp at the bottom — pick a ready message or chat with our team. Phone: +91 90904 48890',
    langPickerTitle: 'Choose your language',
    langPickerSubtitle: 'మీ భాష ఎంచుకోండి',
    langPickerHint: 'You can change this anytime from the Language button at the top',
    helpGuideTitle: 'How to use this site',
    helpGuideSubtitle: '5 simple steps — read once and you\'re set',
    helpGuideOpenFaq: 'See frequently asked questions',
    helpGuideCallWa: 'Get help on WhatsApp',
    helpStep1Title: 'Change language',
    helpStep1Body: 'Tap 🌐 Language at the top right and pick your language.',
    helpStep2Title: "Read today's paper",
    helpStep2Body: 'Tap "Today\'s paper" at the bottom — your daily e-paper opens.',
    helpStep3Title: 'Make text bigger',
    helpStep3Body: 'Tap the + button at the top to make text bigger.',
    helpStep4Title: 'Sign in / subscribe',
    helpStep4Body: 'Tap Sign in at the top for the full paper — Google login, ₹999/year.',
    helpStep5Title: 'Need help?',
    helpStep5Body: 'Tap WhatsApp at the bottom — our team will guide you.',
    easyReadingLabel: 'Easy reading',
    easyReadingOn: 'On',
    easyReadingOff: 'Off',
    navHelp: 'Help',
    navStories: 'Stories',
    nativeYou: 'You',
    nativeToday: 'Today',
    nativeGuest: 'Guest',
    nativeGuestHint: 'Preview two pages — sign in for the full paper',
    nativeMember: 'Member',
    nativePrefs: 'Preferences',
    nativeSupport: 'Support',
    nativeLegal: 'Legal',
    nativeShareApp: 'Share App',
    nativeShare: 'Share',
    nativeEditionAlerts: 'New edition alerts',
    nativeOfflineHint: 'Available offline',
    nativeBookmarks: 'Saved',
    nativeBookmark: 'Save',
    nativeBookmarkSaved: 'Saved',
    nativeSearch: 'Search',
    nativeSearchPlaceholder: 'Stories or paper date…',
    nativeSearchHint: 'Type at least 2 characters',
    nativeSearchEmpty: 'No results',
    nativeTheme: 'Appearance',
    nativeThemeSystem: 'System',
    nativeThemeLight: 'Light',
    nativeThemeDark: 'Dark',
    nativePassActive: 'Active',
    nativePassGet: 'Get',
    nativeUnlockHint: 'Unlock the daily e-paper, archive, and stories',
    nativeDone: 'Done',
    nativeMarketsTitle: 'Market rates',
    nativeMarketsToday: 'Today',
    nativeMarketsLive: 'Today’s rate',
    nativeMarketGold: 'Gold',
    nativeMarketSilver: 'Silver',
    nativeMarketPetrol: 'Petrol',
    nativeMarketDiesel: 'Diesel',
    nativeMarketTG: 'Telangana',
    nativeMarketAP: 'Andhra',
    nativeBackToMenu: 'Back to Menu',
    nativeBackToApp: 'Back to app',
    nativeValidUntil: 'Valid until',
    nativeTrialUntil: 'Trial until',
    toiTopNews: 'Top News',
    navEpaper: 'E-Paper',
    navVideos: 'Videos',
    navMenu: 'Menu',
    textSize_sm_hint: 'Small',
    textSize_md_hint: 'Normal',
    textSize_lg_hint: 'Large',
    referralTitle: 'Invite friends',
    referralFabLabel: 'Invite',
    referralLead: 'Share your link — friends get 2 days of free full access.',
    referralCopy: 'Copy link',
    referralCopied: 'Copied!',
    referralClaimPlaceholder: "Friend's code",
    referralClaimBtn: 'Activate trial',
    referralClaimSuccess: '2-day trial activated!',
    referralClaimFail: 'Invalid code or already used.',
    referralShareWa: 'Share on WhatsApp',
    referralShareText: 'Repatikosam — Telugu daily e-paper',
    referralShareCode: 'Referral code',
    referralTrialActive: 'Your trial is active until {date}',
    installIosHint: 'Tap Share → Add to Home Screen for one-tap reading',
    installIosShort: 'Add to Home Screen',
    installDesktopShort: 'Install the desktop app',
    installSideTab: 'Install',
    installDesktopBtn: 'Install Desktop App',
    tickerLabel: '🔴 Breaking',
    headlines: [
      'AP CM Chandrababu Naidu announces new development schemes',
      'Roads & Highways developed by Chandrababu Naidu',
      'Land prices surged significantly after 2024',
      'New turn in Andhra Pradesh politics',
      'Repatikosam Daily — Today\'s Headlines',
    ],
    todayPaper: '📰 Today\'s Paper',
    paperHeadline: 'Daily Newspaper',
    paperSub: 'Honestly, for the future of the Telugu nation...',
    readBtn: 'Read the paper',
    readDailyBtn: "Read today's paper",
    readMonthlyBtn: "Read this month's magazine",
    browsePastDaily: 'Older daily papers',
    browsePastMonthly: 'Older monthly magazines',
    pastIssues: 'Older daily papers',
    monthlyCalSub: "Choose which month's magazine to read",
    dailyEdition: 'Daily paper',
    monthlyEdition: 'Monthly Magazines',
    thisMonth: "📖 This Month's Magazine",
    monthlyHeadline: 'Monthly Magazines',
    categories: [
      { label: 'National / International', icon: '🌐' },
      { label: 'Sports', icon: '🏏' },
      { label: 'Entertainment', icon: '🎬' },
    ],
    viewBtn: 'View',
    videosTitle: 'Repatikosam Videos',
    shortsTitle: 'Repatikosam Shorts',
    viewAll: 'View All →',
    aboutTitle: 'About Repatikosam',
    aboutText: "Contemporary politics has become polluted and the future of the Telugu nation hangs by a thread. It is the responsibility of every individual to ensure that our future generations do not become sacrifices in this political game. To enlighten the public with a realistic perspective rather than through political lenses, we have launched 'Repatikosam' daily newspaper and monthly magazine.",
    adsTitle: 'Advertisements',
    adSponsored: 'Sponsored',
    adPartnerStrip: 'Our partners',
    home: 'Home',
    about: 'About Us',
    contact: 'Contact',
    rights: 'All rights reserved.',
    trustStrip: 'Public-interest journalism • Fact-first editorial standards • Responsible publishing',
    footerMadeInAp: 'Editorial digital platform based in Amaravati, Andhra Pradesh.',
    nav: ['Home', 'This paper', 'Old papers', 'Poll'],
    navMore: 'More',
    navWhatsApp: 'WhatsApp',
    calTitle: 'Older daily papers',
    calSub: 'Choose which day to read',
    calRecent: 'Recent papers',
    calAvailable: 'Paper available',
    calToday: 'Today',
    months: ['January','February','March','April','May','June','July','August','September','October','November','December'],
    archiveTitle: 'E-Paper Archive',
    archiveIssues: 'Issues',
    archiveEmpty: 'No papers uploaded yet',
    pollTitle: 'Your Opinion',
    pollHeadHint: 'Tap one option',
    pollVotes: 'votes',
    pollThanks: '✅ Your vote has been recorded!',
    statEditions: 'Editions',
    statEst: 'Established',
    statLang: 'Telugu',
    statLangLabel: 'Language',
    statState: 'Andhra Pradesh',
    statStateLabel: 'State',
    latestNews: 'Latest News',
    articlesTitle: 'Exclusive Stories',
    readMore: 'Read More',
    ytDesc: 'Honestly, for the future of the Telugu nation...',
    ytSubscribe: 'Subscribe',
    ytVideos: 'Videos',
    ytShorts: 'Shorts',
    ytFeatured: 'Latest Video',
    ytTitle: 'Repatikosam YouTube',
    ytNowPlaying: 'Playing',
    ytPlaylist: 'Videos',
    zonesLabel: 'AP Publication Centers',
    zonesLead: 'From coastal Andhra to Rayalaseema — one paper, six publication centers.',
    zones: [
      { icon: '🕰️', name: 'Guntur' },
      { icon: '🌉', name: 'Vijayawada' },
      { icon: '🗼', name: 'Srikakulam' },
      { icon: '🌊', name: 'Visakhapatnam' },
      { icon: '🌴', name: 'Rajamahendravaram' },
      { icon: '🏰', name: 'Kadapa' },
    ],
    brandName: 'Repatikosam',
    legalEntityName: 'M S Publications',
    legalEntityLabel: 'Registered business',
    tradeNameLabel: 'Trade name',
    gstLabel: 'GSTIN',
    businessTypeLabel: 'Business type',
    youtube: 'YouTube',
    facebook: 'Facebook',
    instagram: 'Instagram',
    mobileAppEyebrow: 'Mobile app',
    mobileAppTitle: 'Android & iOS — coming soon',
    mobileAppSub: 'Download on Google Play and the App Store in 1–2 weeks',
    mobileAppPlay: 'Google Play',
    mobileAppIos: 'App Store',
    mobileAppSoon: 'Soon',
    footerQuickLinks: 'Quick Links',
    footerCompany: 'Company',
    footerLegal: 'Legal',
    footerContact: 'Contact',
    footerTerms: 'Terms & Conditions',
    footerPrivacy: 'Privacy Policy',
    footerRefund: 'Refund Policy',
    footerCancellation: 'Cancellation Policy',
    legalBackHome: '← Home',
    legalLangEn: 'English',
    legalLangTe: 'తెలుగు',
    legalTeNote: 'Full legal text is available in English.',
    legalPoliciesNav: 'Policies & help',
    legalHeroSub: 'Official policies for Repatikosam, operated by M S Publications.',
    contactPageHeroSub: 'Reach us for subscription, payment, or e-paper support.',
    contactFormSub: 'Your details open in WhatsApp ready to send — you can edit before sending.',
    cookieConsentTitle: 'Cookie preferences',
    cookieConsentText: 'We use analytics cookies to improve the site. Accept or reject optional cookies.',
    cookieConsentAccept: 'Accept',
    cookieConsentReject: 'Reject',
    editionNotifyPrompt: 'Get notified when a new edition is published?',
    editionNotifyEnable: 'Allow',
    editionNotifyLater: 'Later',
    editionNotifyTitle: 'Repatikosam — new edition',
    editionNotifyBody: "Today's e-paper is now available.",
    subscribeBillingNotice: 'Billed by {business} · GSTIN {gstin}',
    paymentSuccessTitle: 'Payment successful!',
    paymentSuccessBody: 'Your subscription is active. Start reading the e-paper.',
    paymentSuccessUntil: 'Your access is valid until {date}.',
    paymentSuccessReceipt: 'Use the billing receipt link below. Email is sent automatically when SMTP is configured.',
    paymentSuccessEmailSent: 'Billing receipt emailed to {email}.',
    paymentSuccessReceiptLink: 'View / download billing receipt',
    paymentSuccessCta: 'Read the paper →',
    subProcessingTitle: 'Processing payment…',
    subProcessingText: 'Unlocking your access…',
    subUnlockHeadline: 'Unlock the full edition',
    subUnlockLead: 'Annual membership tied to your Google account — daily e-paper, archives, and every story.',
    subBullet1: 'Daily digital newspaper',
    subBullet2: 'Monthly magazine archive',
    subBullet3: 'All devices, one account',
    subBullet4: '365 days of access',
    subModalTitle: 'Subscribe to continue',
    subModalSub: 'Unlock the full e-paper & archive',
    subSignedInAs: 'Signed in as',
    subSwitchAccount: 'Use a different account',
    subAnnualPass: 'Annual pass',
    subPerYear: '/ year',
    subLinkedGmail: 'Subscription is linked to this Gmail only.',
    subPlanPrice: 'Plan price',
    subGstLabel: 'GST (18%)',
    subTotalDue: 'Total due',
    subPleaseWait: 'Please wait…',
    subPayUnlock: 'Pay ₹{total} & unlock',
    subInstantAccess: 'Instant access after successful payment',
    subWhatsappLabel: 'WhatsApp number',
    subWhatsappPlaceholder: '9876543210',
    subWhatsappOptIn: 'Send me a WhatsApp when today’s paper is live',
    subWhatsappRequired: 'Enter a valid 10-digit WhatsApp number',
    subWhatsappOptInRequired: 'Please allow WhatsApp alerts for today’s paper',
    subSecuredRazorpay: 'Secured by Razorpay · UPI, cards, netbanking',
    subDemoMode: 'Demo mode — add Razorpay keys on the server to accept real payments.',
    subTestModeTitle: 'Test payment mode',
    subTestModeUpiNote: 'The UPI QR will show invalid if you scan with real GPay / PhonePe — that is normal in test mode.',
    subTestModeUpiItem: 'UPI: pay with success@razorpay',
    subTestModeCardItem: 'Card: 4111 1111 1111 1111, any future expiry, any CVV',
    subTestModeFoot: 'For real QR payments, switch to Live keys in Razorpay after KYC.',
    subPaymentCancelled: 'Payment cancelled',
    subPaymentError: 'Payment could not be completed. Please try again.',
    subServerError: 'Cannot reach the server. Check your connection and try again.',
    subInvalidPayment: 'Invalid payment response from server.',
    subVerifyFailed: 'Payment verification failed',
    subOrderError: 'Could not start payment. Please try again.',
    feedErrorMsg: 'Could not load stories. Check your connection.',
    feedErrorRetry: 'Try again',
    footerLocation: 'Amaravati, Andhra Pradesh',
    insideLabel: 'In this edition',
    insidePolitics: 'Politics',
    insideAgri: 'Agriculture',
    insideEdu: 'Education',
    insideNational: 'National',
    insideIntl: 'International',
    insideSports: 'Sports',
    yearsLabel: 'Years',
    issuesLabel: 'Editions',
    readersLabel: 'Readers',
    loading: 'Loading...',
    paperNotAvailable: 'Paper not available for this date',
    serverError: 'Server error. Please try again.',
    closeBtn: 'Close',
    calDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    catAll: 'All',
    catPolitics: 'Politics',
    catNews: 'News',
    catTurmeric: 'Legacy Of TDP',
    listenBtn: 'Listen',
    stopListen: 'Stop',
    readAloudTitle: 'Read aloud',
    zonesCountSuffix: 'Centers',
    pollLangTip: 'Poll question is in Telugu',
    sharePaperBadge: 'Paper',
    shareWhatsAppTitle: 'Share on WhatsApp',
    paperViewerTitle: 'Repatikosam Paper',
    shareMessage: 'Repatikosam Daily',
    paperScrollHint: 'pages — scroll down',
    paperPrev: 'Previous',
    paperNext: 'Next',
    paperPageOf: 'Page',
    paperViewScroll: 'Scroll',
    paperViewPages: 'Pages',
    paperScrollAllHint: 'Scroll down to see all pages',
    paperZoomLabel: 'Zoom',
    paperZoomIn: 'Zoom in',
    paperZoomOut: 'Zoom out',
    paperZoomReset: 'Reset',
    paperRetry: 'Try again',
    paperSwipeHint: 'Swipe left or right to change pages',
    paperPinchHint: 'Pinch to zoom · drag to see sides (page swipe off while zoomed)',
    installTitle: 'Repatikosam',
    installSub: 'Install as app',
    installBtn: 'Install',
    dismissBtn: 'Dismiss',
    aboutHeroTagline: 'Honestly, for the future of the Telugu nation...',
    aboutSectionHeading: 'About us',
    roleManagingDirector: 'Managing Director',
    roleChiefEditor: 'Chief Editor',
    aboutLeadershipHeading: 'Our Leadership',
    aboutStatMonthly: 'Monthly magazine',
    aboutStatDaily: 'Daily',
    aboutStatState: 'Andhra Pradesh',
    aboutStatStateLabel: 'State',
    aboutSocialMedia: 'Social media',
    contactAddressLabel: 'Address',
    contactAddress: 'Door No. 5-82-25, 4th Lane, Pandaripuram, Guntur, Andhra Pradesh 522006, India',
    contactEmailLabel: 'Email',
    contactFormTitle: 'Send a message',
    contactNameLabel: 'Name',
    contactPhoneLabel: 'Phone number',
    contactWhatsAppLabel: 'WhatsApp',
    contactReachUs: 'Reach us',
    contactBusinessTitle: 'Registered business',
    contactIntlNoteLabel: 'International customers',
    contactIntlNote:
      'We accept payments from India and international customers via Razorpay (INR). Your bank may apply currency conversion fees on international cards.',
    contactMessageLabel: 'Message',
    contactNamePlaceholder: 'Your name',
    contactPhonePlaceholder: 'Your phone number',
    contactMessagePlaceholder: 'Your message...',
    contactSentSuccess: 'Your message was sent to WhatsApp!',
    contactSendAgain: 'Send again',
    contactSendViaWa: 'Send via WhatsApp',
    contactWaIntro: 'Message from Repatikosam website:',
    contactWaName: 'Name',
    contactWaPhone: 'Phone',
    contactWaMessage: 'Message',
    waSuggestTitle: 'How can we help?',
    waSuggestSub: 'Pick one — your WhatsApp message will be ready to send',
    waSuggestSendHint: 'WhatsApp opens with your message — tap Send to contact us',
    waSuggestClose: 'Cancel',
    waSuggest_subscribe: 'How do I subscribe?',
    waSuggest_subscribe_msg:
      'Hello, I would like details on how to get Repatikosam membership (₹999).',
    waSuggest_payment: 'Paid but paper not opening',
    waSuggest_payment_msg:
      'Hello, I paid but cannot open the full paper. Please help.',
    waSuggest_login: 'Login / sign-up problem',
    waSuggest_login_msg: 'Hello, I cannot log in with Google. Please help.',
    waSuggest_paper: 'Paper not loading',
    waSuggest_paper_msg: "Hello, today's paper is not loading for me. Please check.",
    waSuggest_refund: 'Refund / payment receipt',
    waSuggest_refund_msg: 'Hello, I need help with a refund or payment receipt.',
    waSuggest_language: 'How to change language?',
    waSuggest_language_msg:
      'Hello, I need help changing the language on the Repatikosam website.',
    waSuggest_guide: 'How to use the site?',
    waSuggest_guide_msg:
      'Hello, can you guide me on how to use the Repatikosam website?',
    waSuggest_other: 'Other help',
    waSuggest_other_msg: 'Hello, I need help with Repatikosam.',
    headerTaglineFull: 'Honestly, fearlessly, selflessly — for the Telugu nation',
    headerEditionLive: 'LIVE',
    headerEditionSealTop: 'Repatikosam',
    headerEditionSealBottom: 'Daily',
    artScrollHint: '← scroll →',
    noStoriesYet: 'Stories will appear here soon.',
    poweredBy: 'Powered by',
    skipToContent: 'Skip to content',
    loadingVideos: 'Loading videos…',
  },

  ta: {
    location: '📍 அமராவதி, ஆந்திரப் பிரதேசம்',
    logout: 'வெளியேறு',
    signInCopy: 'Google மூலம் உள்நுழைந்து e-paper படிக்கவும், கருத்துக்கணிப்பில் வாக்களியுங்கள், சிறப்புக் கதைகளைத் திறக்கவும். முழு அணுகலுக்கு சந்தா செய்யுங்கள்.',
    signInLoading: 'உள்நுழைகிறது…',
    signInBtn: 'Google உடன் தொடரவும்',
    signInEdition: 'டிஜிட்டல் பதிப்பு · 2026',
    signInLabel: 'உறுப்பினர் உள்நுழைவு',
    signInHeadline: 'செய்தி அறைக்குள்',
    signInHeadlineEm: 'நுழையுங்கள்',
    signInMetaPerYear: 'ஆண்டுக்கு',
    signInMetaEpaper: 'E-paper',
    signInMetaEpaperSub: 'தினசரி + காப்பகம்',
    signInMetaSecure: 'பாதுகாப்பானது',
    signInMetaSecureSub: 'Google மட்டும்',
    signInLegal: 'உள்நுழைவதன் மூலம் எங்கள் விதிமுறைகளை ஏற்கிறீர்கள். Google-இலிருந்து உங்கள் பெயர் மற்றும் மின்னஞ்சலை மட்டும் சேமிக்கிறோம்.',
    signInTickerTruth: 'நேர்மையான இதழியல்',
    signInTickerEpaper: 'தெலுங்கு e-paper',
    signInTickerEditions: 'தினசரி பத்திரிகை & மாத இதழ்',
    helpStep2Title: 'Google மூலம் உள்நுழையவும்',
    paperSliderAria: 'பக்க ஸ்லைடர்',
    paperPagesAria: 'பக்கங்கள்',
    waSuggestSendHint: 'WhatsApp திறக்கும் — செய்தி அனுப்பத் தயாராக இருக்கும்',
    testimonialsSub: 'ரேபதிகோசம் பற்றி வாசகர்கள் சொல்வது',
    searchHowItWorks: 'இந்த தளத்தை எப்படிப் பயன்படுத்துவது',
    login: 'உள்நுழைவு',
    signInErrorApiDev: 'API இயங்கவில்லை. மற்றொரு டெர்மினலில்: npm run server',
    signInErrorCancelled: 'Google உள்நுழைவு ரத்து செய்யப்பட்டது அல்லது தடுக்கப்பட்டது.',
    signInErrorNetwork: 'சேவையகத்தை அணுக முடியவில்லை. இணைப்பைச் சரிபார்த்து மீண்டும் முயலவும்.',
    signInErrorFailed: 'Google உள்நுழைவு தோல்வி. மீண்டும் முயலவும்.',
    launchBadge: '5 Aug launch',
    launchLiveBadge: 'Live',
    launchTitle: '5 August 2026 — Official Repatikosam website launch',
    launchSub: 'Honestly · Fearlessly · Selflessly',
    launchLiveTitle: 'Repatikosam is officially live!',
    launchLiveSub: 'Welcome to the official Repatikosam website',
    launchCta: 'View the paper',
    launchDays: 'Days',
    launchHours: 'Hours',
    launchMins: 'Mins',
    launchSecs: 'Secs',
    launchDismiss: 'Dismiss',
    marketingBadge: 'Annual pass',
    marketingPromoText: 'Daily e-paper + archive — ₹999/year (+ GST)',
    marketingSubscribeBtn: 'இப்போது சந்தா செய்யுங்கள்',
    marketingShareBtn: 'Share on WhatsApp',
    marketingShareText: 'Repatikosam Telugu daily — honest news & digital e-paper',
    marketingSectionEyebrow: 'Digital membership',
    marketingSectionTitle: 'Unlock the full edition',
    marketingSectionLead: 'Papers, polls, and exclusive stories — one year of full access.',
    marketingPerk1: 'Daily e-paper & monthly magazine',
    marketingPerk2: 'Past editions archive',
    marketingPerk3: 'Polls & exclusive stories',
    marketingPerk4: 'Read on all devices',
    marketingPerYear: 'year',
    marketingGstNote: '+ 18% GST · Secured by Razorpay',
    marketingSecureNote: 'Instant access after payment',
    marketingFloatSubscribe: 'சந்தா',
    sidebarSubscribeLead: 'முழு e-paper அணுகல்',
    previewBtn: 'Try first 2 pages free',
    previewEyebrow: 'Free',
    previewTitle: 'Read the first 2 pages',
    previewSub: 'No subscription needed — try the paper now',
    previewBtnAction: 'Start free read',
    previewLimitBanner: 'Preview ended — subscribe for full edition',
    previewSubscribeCta: 'சந்தா செய்து தொடரவும்',
    socialProofTitle: 'Readers trust Repatikosam',
    socialProofReaders: 'Readers',
    socialProofSubs: 'Members',
    socialProofLeads: 'Sample downloads',
    testimonialsEyebrow: 'Reader voices',
    testimonialsTitle: 'What readers say about Repatikosam',
    testimonial1: 'Honest Telugu news and daily e-paper.',
    testimonial1Author: 'Reader, Andhra Pradesh',
    testimonial1Location: 'Andhra Pradesh',
    testimonial2: 'Monthly magazine plus archive in one place.',
    testimonial2Author: 'Reader, Telangana',
    testimonial2Location: 'Telangana',
    testimonial3: 'Reliable Telugu news every morning.',
    testimonial3Author: 'Reader, Guntur',
    testimonial3Location: 'Guntur, Andhra Pradesh',
    testimonial4: 'Great archive access in one place.',
    testimonial4Author: 'Reader, Visakhapatnam',
    testimonial4Location: 'Visakhapatnam, Andhra Pradesh',
    leadTitle: 'Free sample PDF',
    leadLead: 'Enter your email to download the sample booklet.',
    leadEmailPlaceholder: 'you@email.com',
    leadSubmit: 'Get download',
    leadSuccess: 'Thank you! PDF opens in a new tab.',
    leadError: 'Please enter a valid email.',
    faqTitle: 'FAQ',
    faqSubtitle: 'Membership, payments, and e-paper help',
    trustRibbonEyebrow: 'நம்பிக்கை',
    trustRibbonAria: 'நம்பிக்கை அளவீடுகள்',
    trustSince: 'Since 2015',
    trustSinceSub: 'முதல் வெளியீடு',
    trustReaders: 'வாசகர்கள்',
    trustReadersSub: 'வளரும் சமூகம்',
    trustDaily: 'Daily e-paper',
    trustDailySub: 'தினசரி பதிப்பு',
    trustLocation: 'Amaravati · AP',
    todayHeadlinesTitle: "Today's headlines",
    editorsPickTitle: "Editor's pick",
    editorsPickBadge: "Editor's pick",
    searchEyebrow: 'தேடல்',
    searchTitle: 'பதிப்புகள் & கதைகள்',
    searchMinHint: 'குறைந்தது 2 எழுத்துகள் தட்டச்சு செய்யவும்',
    searchClear: 'அழி',
    searchResultsLabel: 'முடிவுகள்',
    searchEditions: 'பதிப்புகள்',
    searchStories: 'கதைகள்',
    searchPlaceholder: 'Search headlines…',
    searchNoResults: 'No results',
    guestBarTitle: 'முழு அணுகல் பெறுங்கள்',
    guestBarText: 'இன்றைய e-paper, காப்பகம் மற்றும் சிறப்புக் கதைகள்',
    guestBarSub: 'ஆண்டுக்கு ₹999 · GST கூடுதல்',
    guestBarPerkPages: 'முழு பக்கங்கள்',
    guestBarPerkStory: 'சிறப்புக் கதைகள்',
    guestBarPerkFull: 'முழு அணுகல்',
    guestBarLater: 'பிறகு',
    guestBarBtn: 'சந்தா செய்யுங்கள்',
    headerSignIn: 'உள்நுழைவு',
    textSizeLabel: 'Text size',
    textSize_sm: 'A',
    textSize_md: 'A+',
    textSize_lg: 'A++',
    editionLiveTitle: "Today's edition is live!",
    editionLiveCta: 'Read the paper',
    shareStoryBtn: 'Share on WhatsApp',
    editionToastTitle: "Today's edition is live!",
    editionToastBody: 'Read today\'s e-paper now.',
    editionToastRead: 'Open paper',
    storyUsefulPrompt: 'Helpful?',
    storyUsefulYes: 'Yes',
    storyUsefulThanks: 'Thanks!',
    relatedStoriesTitle: 'Related',
    freeReadBadge: 'Free',
    subscribersOnlyBadge: 'சந்தாதாரர்களுக்கு மட்டும்',
    categoryNewsTitle: 'By category',
    categoryViewAll: 'View all',
    whatsappChannel: 'WhatsApp Channel',
    youtubeWatchNote: 'Videos + news together',
    faq1q: 'Subscription price?',
    faq1a: '₹999/year + GST. Full digital access.',
    faq2q: 'Access after payment?',
    faq2a: 'Instant after Razorpay payment.',
    faq3q: 'Devices?',
    faq3a: 'Phone, tablet, desktop. Add to home screen.',
    faq4q: 'Referral?',
    faq4a: 'Friends get 2-day trial via your link.',
    faq5q: 'Refund?',
    faq5a: 'Contact contact@repatikosam.com',
    faq6q: 'How to change language?',
    faq6a: 'Tap 🌐 Language at top right — Telugu, English, Tamil, or Kannada.',
    faq7q: "Where is today's paper?",
    faq7a: 'Tap "Today\'s paper" in the bottom bar or the paper preview on the page.',
    faq8q: 'How to make text bigger?',
    faq8a: 'Tap + at the top to make text bigger. Tap − to make it smaller.',
    faq9q: 'How to get help?',
    faq9a: 'Tap WhatsApp at the bottom. Phone: +91 90904 48890',
    langPickerTitle: 'Choose your language',
    langPickerSubtitle: 'உங்கள் மொழியைத் தேர்ந்தெடுக்கவும்',
    langPickerHint: 'You can change language anytime from the top',
    helpGuideTitle: 'தள வழிகாட்டி',
    helpGuideSubtitle: '5 simple steps',
    helpGuideOpenFaq: 'See FAQ',
    helpGuideCallWa: 'WhatsApp help',
    helpStep1Title: 'மொழியைத் தேர்ந்தெடுக்கவும்',
    helpStep1Body: 'Tap 🌐 Language at the top right.',
    helpStep2Body: 'Tap "Today\'s paper" at the bottom.',
    helpStep3Title: 'Bigger text',
    helpStep3Body: 'Tap + at the top to make text bigger.',
    helpStep4Title: 'Sign in',
    helpStep4Body: 'Tap Sign in for full access — Google login.',
    helpStep5Title: 'இன்றைய பத்திரிகையைப் படிக்கவும்',
    helpStep5Body: 'Tap WhatsApp — our team will help.',
    easyReadingLabel: 'Easy reading',
    easyReadingOn: 'On',
    easyReadingOff: 'Off',
    navHelp: 'உதவி',
    navStories: 'கதைகள்',
    nativeYou: 'நீங்கள்',
    nativeToday: 'இன்று',
    nativeGuest: 'விருந்தினர்',
    nativeGuestHint: 'முன்னோட்டம் பார்க்கவும் — முழு பத்திரிகைக்கு உள்நுழையவும்',
    nativeMember: 'உறுப்பினர்',
    nativePrefs: 'விருப்பங்கள்',
    nativeSupport: 'உதவி',
    nativeLegal: 'சட்டம்',
    nativeShareApp: 'ஆப்பை பகிரவும்',
    nativeShare: 'பகிர்',
    nativeEditionAlerts: 'புதிய பதிப்பு அறிவிப்பு',
    nativeOfflineHint: 'ஆஃப்லைனில் படிக்கலாம்',
    nativeBookmarks: 'சேமித்தவை',
    nativeBookmark: 'சேமி',
    nativeBookmarkSaved: 'சேமிக்கப்பட்டது',
    nativeSearch: 'தேடல்',
    nativeSearchPlaceholder: 'செய்திகள் அல்லது தேதி…',
    nativeSearchHint: 'குறைந்தது 2 எழுத்துகள்',
    nativeSearchEmpty: 'முடிவுகள் இல்லை',
    nativeTheme: 'தோற்றம்',
    nativeThemeSystem: 'சிஸ்டம்',
    nativeThemeLight: 'லைட்',
    nativeThemeDark: 'டார்க்',
    nativePassActive: 'Active',
    nativePassGet: 'Get',
    nativeUnlockHint: 'இன்றைய இ-பேப்பர், ஆர்க்கைவ், கதைகளை திறக்கவும்',
    nativeDone: 'முடிந்தது',
    nativeMarketsTitle: 'சந்தை விலை',
    nativeMarketsToday: 'இன்று',
    nativeMarketsLive: 'இன்றைய விலை',
    nativeMarketGold: 'தங்கம்',
    nativeMarketSilver: 'வெள்ளி',
    nativeMarketPetrol: 'பெட்ரோல்',
    nativeMarketDiesel: 'டீசல்',
    nativeMarketTG: 'தெலங்காணா',
    nativeMarketAP: 'ஆந்திரா',
    nativeBackToMenu: 'மெனுவுக்கு',
    nativeBackToApp: 'ஆப்பிற்கு',
    nativeValidUntil: 'செல்லுபடியாகும்',
    nativeTrialUntil: 'சோதனை வரை',
    toiTopNews: 'முக்கிய செய்தி',
    navEpaper: 'இ-பேப்பர்',
    navVideos: 'வீடியோ',
    navMenu: 'மெனு',
    textSize_sm_hint: 'Small',
    textSize_md_hint: 'Normal',
    textSize_lg_hint: 'Large',
    referralTitle: 'Invite friends',
    referralFabLabel: 'Invite',
    referralLead: 'Share your link for 2-day friend trial.',
    referralCopy: 'Copy link',
    referralCopied: 'Copied!',
    referralClaimPlaceholder: 'Friend code',
    referralClaimBtn: 'Activate trial',
    referralClaimSuccess: 'Trial activated!',
    referralClaimFail: 'Invalid code.',
    referralShareWa: 'Share on WhatsApp',
    referralShareText: 'Repatikosam Telugu daily',
    referralShareCode: 'Code',
    referralTrialActive: 'Trial until {date}',
    installIosHint: 'Share → Add to Home Screen',
    installIosShort: 'Add to Home Screen',
    installDesktopShort: 'Install the desktop app',
    installSideTab: 'Install',
    installDesktopBtn: 'Install Desktop App',
    tickerLabel: '🔴 முக்கியச் செய்தி',
    headlines: [
      'ஆந்திர முதல்வர் சந்திரபாபு நாயுடு புதிய திட்டங்கள்',
      'சந்திரபாபு நாயுடு உருவாக்கிய சாலைகள் & நெடுஞ்சாலைகள்',
      '2024க்கு பிறகு நில விலைகள் கணிசமாக உயர்ந்தன',
      'ஆந்திரப் பிரதேச அரசியலில் புதிய திருப்பம்',
      'ரேபடிகோசம் நாளிதழ் - இன்றைய தலைப்புச் செய்திகள்',
    ],
    todayPaper: '📰 இன்றைய பத்திரிகை',
    paperHeadline: 'தினசரி செய்தித்தாள்',
    paperSub: 'தெலுங்கு தேசத்தின் எதிர்காலத்திற்காக நேர்மையாக...',
    readBtn: 'செய்தித்தாள் படியுங்கள்',
    readDailyBtn: 'இன்றைய செய்தித்தாள் படியுங்கள்',
    readMonthlyBtn: 'இந்த மாத இதழைப் படியுங்கள்',
    browsePastDaily: 'பழைய நாள் செய்தித்தாள்கள்',
    browsePastMonthly: 'பழைய மாத இதழ்கள்',
    pastIssues: 'பழைய நாள் செய்தித்தாள்கள்',
    monthlyCalSub: 'எந்த மாத இதழைப் படிக்க வேண்டும் என்பதைத் தேர்ந்தெடுக்கவும்',
    dailyEdition: 'தினசரி செய்தித்தாள்',
    monthlyEdition: 'மாத இதழ்கள்',
    thisMonth: '📖 இந்த மாத இதழ்',
    monthlyHeadline: 'மாத இதழ்கள்',
    categories: [
      { label: 'தேசிய / சர்வதேசம்', icon: '🌐' },
      { label: 'விளையாட்டு', icon: '🏏' },
      { label: 'பொழுதுபோக்கு', icon: '🎬' },
    ],
    viewBtn: 'பார்க்கவும்',
    videosTitle: 'ரேபடிகோசம் வீடியோக்கள்',
    shortsTitle: 'ரேபடிகோசம் ஷார்ட்ஸ்',
    viewAll: 'அனைத்தும் பார்க்கவும் →',
    aboutTitle: 'ரேபடிகோசம் பற்றி',
    aboutText: "தற்கால அரசியல் மாசுபட்டு தெலுங்கு தேசத்தின் எதிர்காலம் கத்தி முனையில் தொங்குகிறது. அரசியல் விஷ விளையாட்டில் நம் எதிர்கால தலைமுறைகள் பலியாகாமல் பார்க்க வேண்டிய பொறுப்பு ஒவ்வொருவருக்கும் உள்ளது. அரசியல் கண்ணாடிகளின் வழியாக அல்லாமல் யதார்த்த கண்ணோட்டத்தில் மக்களுக்கு விழிப்புணர்வு ஏற்படுத்த 'ரேபடிகோசம்' நாளிதழ், மாத இதழ் தொடங்கப்பட்டது.",
    adsTitle: 'விளம்பரங்கள்',
    adSponsored: 'ஸ்பான்சர்',
    adPartnerStrip: 'எங்கள் கூட்டாளர்கள்',
    home: 'முகப்பு',
    about: 'எங்களைப் பற்றி',
    contact: 'தொடர்பு கொள்ளுங்கள்',
    rights: 'அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.',
    trustStrip: 'பொது நல பத்திரிகை • உண்மை முதன்மை • பொறுப்பான வெளியீடு',
    footerMadeInAp: 'அமராவதி, ஆந்திரப் பிரதேசத்தில் செயல்படும் ஆசிரியர் தளம்.',
    nav: ['முகப்பு', 'இந்த பத்திரிகை', 'பழைய பத்திரிகைகள்', 'கருத்து கணிப்பு'],
    navMore: 'மேலும்',
    navWhatsApp: 'WhatsApp',
    calTitle: 'பழைய நாள் செய்தித்தாள்கள்',
    calSub: 'எந்த நாள் படிக்க வேண்டும் என்பதைத் தேர்ந்தெடுக்கவும்',
    calRecent: 'சமீபத்திய பத்திரிகைகள்',
    calAvailable: 'பத்திரிகை கிடைக்கிறது',
    calToday: 'இன்று',
    months: ['ஜனவரி','பிப்ரவரி','மார்ச்','ஏப்ரல்','மே','ஜூன்','ஜூலை','ஆகஸ்ட்','செப்டம்பர்','அக்டோபர்','நவம்பர்','டிசம்பர்'],
    archiveTitle: 'E-பேப்பர் காப்பகம்',
    archiveIssues: 'இதழ்கள்',
    archiveEmpty: 'இன்னும் பத்திரிகைகள் பதிவேற்றப்படவில்லை',
    pollTitle: 'உங்கள் கருத்து',
    pollHeadHint: 'ஒரு விருப்பத்தைத் தேர்ந்தெடுக்கவும்',
    pollVotes: 'வாக்குகள்',
    pollThanks: '✅ உங்கள் வாக்கு பதிவாகிவிட்டது!',
    statEditions: 'இதழ்கள்',
    statEst: 'தொடங்கிய ஆண்டு',
    statLang: 'தெலுங்கு',
    statLangLabel: 'மொழி',
    statState: 'ஆந்திரப் பிரதேசம்',
    statStateLabel: 'மாநிலம்',
    latestNews: 'சமீப செய்திகள்',
    articlesTitle: 'சிறப்பு கதைகள்',
    readMore: 'மேலும் படிக்கவும்',
    ytDesc: 'தெலுங்கு இனத்தின் எதிர்காலத்திற்காக நேர்மையாக...',
    ytSubscribe: 'சந்தா செய்யுங்கள்',
    ytVideos: 'வீடியோக்கள்',
    ytShorts: 'ஷார்ட்ஸ்',
    ytFeatured: 'சமீபத்திய வீடியோ',
    ytTitle: 'ரேபடிகோசம் YouTube',
    ytNowPlaying: 'இயங்குகிறது',
    ytPlaylist: 'வீடியோக்கள்',
    zonesLabel: 'AP வெளியீட்டு மையங்கள்',
    zonesLead: 'ஆந்திரா முதல் முதல் ஆறு வெளியீட்டு மையங்கள்.',
    zones: [
      { icon: '🕰️', name: 'குந்தூர்' },
      { icon: '🌉', name: 'விஜயவாடா' },
      { icon: '🗼', name: 'ஸ்ரீகாகுளம்' },
      { icon: '🌊', name: 'விசாகபட்டினம்' },
      { icon: '🌴', name: 'ராஜமஹேந்திரவரம்' },
      { icon: '🏰', name: 'கடப்பா' },
    ],
    brandName: 'ரேபடிகோசம்',
    legalEntityName: 'M S Publications',
    legalEntityLabel: 'பதிவு செய்யப்பட்ட வணிகம்',
    tradeNameLabel: 'வணிகப் பெயர்',
    gstLabel: 'GSTIN',
    businessTypeLabel: 'வணிக வகை',
    youtube: 'YouTube',
    facebook: 'Facebook',
    instagram: 'Instagram',
    mobileAppEyebrow: 'மொபைல் ஆப்',
    mobileAppTitle: 'Android & iOS — விரைவில்',
    mobileAppSub: '1–2 வாரங்களில் Google Play மற்றும் App Store இல்',
    mobileAppPlay: 'Google Play',
    mobileAppIos: 'App Store',
    mobileAppSoon: 'விரைவில்',
    footerQuickLinks: 'விரைவு இணைப்புகள்',
    footerCompany: 'நிறுவனம்',
    footerLegal: 'சட்டம்',
    footerContact: 'தொடர்பு',
    footerTerms: 'விதிமுறைகள்',
    footerPrivacy: 'தனியுரிமை கொள்கை',
    footerRefund: 'பணத்திரும்ப கொள்கை',
    footerCancellation: 'ரத்து கொள்கை',
    legalBackHome: '← முகப்பு',
    legalLangEn: 'English',
    legalLangTe: 'తెలుగు',
    legalTeNote: 'Full legal text is in English.',
    legalPoliciesNav: 'சட்டக் கொள்கைகள்',
    legalHeroSub: 'Official policies for Repatikosam, operated by M S Publications.',
    contactPageHeroSub: 'நாங்கள் உதவ தயாராக இருக்கிறோம்',
    contactFormSub: 'ஒரு செய்தியை அனுப்புங்கள் — WhatsApp திறக்கும்',
    cookieConsentTitle: 'Cookie preferences',
    cookieConsentText: 'We use analytics cookies to improve the site. Accept or reject optional cookies.',
    cookieConsentAccept: 'Accept',
    cookieConsentReject: 'Reject',
    editionNotifyPrompt: 'Notify when a new edition is live?',
    editionNotifyEnable: 'Allow',
    editionNotifyLater: 'Later',
    editionNotifyTitle: 'Repatikosam — new edition',
    editionNotifyBody: "Today's e-paper is available.",
    subscribeBillingNotice: 'ஒரு முறை ஆண்டு கட்டணம் · தானியங்கி பற்று இல்லை',
    paymentSuccessTitle: 'Payment successful!',
    paymentSuccessBody: 'Subscription active. Read the e-paper now.',
    paymentSuccessUntil: 'Access until {date}.',
    paymentSuccessReceipt: 'Receipt link below.',
    paymentSuccessEmailSent: 'Receipt emailed to {email}.',
    paymentSuccessReceiptLink: 'View billing receipt',
    paymentSuccessCta: 'Read paper →',
    subProcessingTitle: 'Processing payment…',
    subProcessingText: 'Unlocking your access…',
    subUnlockHeadline: 'Unlock the full edition',
    subUnlockLead: 'Annual membership tied to your Google account — daily e-paper, archives, and every story.',
    subBullet1: 'Daily digital newspaper',
    subBullet2: 'Monthly magazine archive',
    subBullet3: 'All devices, one account',
    subBullet4: '365 days of access',
    subModalTitle: 'Subscribe to continue',
    subModalSub: 'Unlock the full e-paper & archive',
    subSignedInAs: 'Signed in as',
    subSwitchAccount: 'Use a different account',
    subAnnualPass: 'Annual pass',
    subPerYear: '/ year',
    subLinkedGmail: 'Subscription is linked to this Gmail only.',
    subPlanPrice: 'Plan price',
    subGstLabel: 'GST (18%)',
    subTotalDue: 'Total due',
    subPleaseWait: 'Please wait…',
    subPayUnlock: 'Pay ₹{total} & unlock',
    subInstantAccess: 'Instant access after successful payment',
    subWhatsappLabel: 'WhatsApp number',
    subWhatsappPlaceholder: '9876543210',
    subWhatsappOptIn: 'Send me a WhatsApp when today’s paper is live',
    subWhatsappRequired: 'Enter a valid 10-digit WhatsApp number',
    subWhatsappOptInRequired: 'Please allow WhatsApp alerts for today’s paper',
    subSecuredRazorpay: 'Secured by Razorpay · UPI, cards, netbanking',
    subDemoMode: 'Demo mode — add Razorpay keys on the server to accept real payments.',
    subTestModeTitle: 'Test payment mode',
    subTestModeUpiNote: 'The UPI QR will show invalid if you scan with real GPay / PhonePe — that is normal in test mode.',
    subTestModeUpiItem: 'UPI: pay with success@razorpay',
    subTestModeCardItem: 'Card: 4111 1111 1111 1111, any future expiry, any CVV',
    subTestModeFoot: 'For real QR payments, switch to Live keys in Razorpay after KYC.',
    subPaymentCancelled: 'Payment cancelled',
    subPaymentError: 'Payment could not be completed. Please try again.',
    subServerError: 'Cannot reach the server. Check your connection and try again.',
    subInvalidPayment: 'Invalid payment response from server.',
    subVerifyFailed: 'Payment verification failed',
    subOrderError: 'Could not start payment. Please try again.',
    feedErrorMsg: 'கதைகளை ஏற்ற முடியவில்லை. இணைப்பைச் சரிபார்க்கவும்.',
    feedErrorRetry: 'Try again',
    footerLocation: 'அமராவதி, ஆந்திரப் பிரதேசம்',
    insideLabel: 'இந்த பதிப்பில்',
    insidePolitics: 'அரசியல்',
    insideAgri: 'விவசாயம்',
    insideEdu: 'கல்வி',
    insideNational: 'தேசிய',
    insideIntl: 'சர்வதேச',
    insideSports: 'விளையாட்டு',
    yearsLabel: 'ஆண்டுகள்',
    issuesLabel: 'பதிப்புகள்',
    readersLabel: 'வாசகர்கள்',
    loading: 'ஏற்றுகிறது...',
    paperNotAvailable: 'இந்த தேதிக்கு பத்திரிகை இல்லை',
    serverError: 'சர்வர் பிழை. மீண்டும் முயற்சிக்கவும்.',
    closeBtn: 'மூடு',
    calDays: ['ஞா', 'தி', 'செ', 'பு', 'வி', 'வெ', 'ச'],
    catAll: 'அனைத்தும்',
    catPolitics: 'அரசியல்',
    catNews: 'செய்திகள்',
    catTurmeric: 'மஞ்சள்',
    listenBtn: 'கேளுங்கள்',
    stopListen: 'நிறுத்து',
    readAloudTitle: 'சத்தமாக படிக்க',
    zonesCountSuffix: 'மையங்கள்',
    pollLangTip: 'வாக்கெடுப்பு தெலுங்கில் உள்ளது',
    sharePaperBadge: 'பத்திரிகை',
    shareWhatsAppTitle: 'WhatsApp இல் பகிர்',
    paperViewerTitle: 'ரேபடிகோசம் பத்திரிகை',
    shareMessage: 'ரேபடிகோசம் நாளிதழ்',
    paperScrollHint: 'பக்கங்கள் — கீழே உருட்டவும்',
    paperPrev: 'முந்தைய',
    paperNext: 'அடுத்த',
    paperPageOf: 'பக்கம்',
    paperViewScroll: 'ஸ்க்ரோல்',
    paperViewPages: 'பக்கங்கள்',
    paperScrollAllHint: 'அனைத்து பக்கங்களும் கீழே உருட்டவும்',
    paperZoomLabel: 'பெரிதாக்கம்',
    paperZoomIn: 'பெரிதாக்கு',
    paperZoomOut: 'சிறிதாக்கு',
    paperZoomReset: 'மீட்டமை',
    paperRetry: 'மீண்டும் முயற்சி',
    paperSwipeHint: 'பக்கங்களை மாற இடது/வலது ஸ்வைப் செய்யுங்கள்',
    paperPinchHint: 'ஜூம் செய்ய இரண்டு விரல்களால் பிஞ்ச் செய்யுங்கள்',
    installTitle: 'ரேபடிகோசம்',
    installSub: 'ஆப் ஆக நிறுவவும்',
    installBtn: 'நிறுவு',
    dismissBtn: 'நிராகரி',
    aboutHeroTagline: 'தெலுங்கு இனத்தின் எதிர்காலத்திற்காக நேர்மையாக...',
    aboutSectionHeading: 'எங்களைப் பற்றி',
    roleManagingDirector: 'Managing Director',
    roleChiefEditor: 'Chief Editor',
    aboutLeadershipHeading: 'எங்கள் தலைமை',
    aboutStatMonthly: 'மாத இதழ்',
    aboutStatDaily: 'நாளிதழ்',
    aboutStatState: 'ஆந்திரப் பிரதேசம்',
    aboutStatStateLabel: 'மாநிலம்',
    aboutSocialMedia: 'சமூக ஊடகம்',
    contactAddressLabel: 'முகவரி',
    contactAddress: 'அமராவதி, ஆந்திரப் பிரதேசம், இந்தியா',
    contactEmailLabel: 'மின்னஞ்சல்',
    contactFormTitle: 'செய்தி அனுப்புங்கள்',
    contactNameLabel: 'பெயர்',
    contactPhoneLabel: 'தொலைபேசி எண்',
    contactWhatsAppLabel: 'WhatsApp',
    contactReachUs: 'எங்களை அணுகவும்',
    contactBusinessTitle: 'வணிக விவரங்கள்',
    contactIntlNoteLabel: 'சர்வதேச வாடிக்கையாளர்கள்',
    contactIntlNote:
      'We accept payments from India and international customers via Razorpay (INR). Your bank may apply currency conversion fees on international cards.',
    contactMessageLabel: 'செய்தி',
    contactNamePlaceholder: 'உங்கள் பெயர்',
    contactPhonePlaceholder: 'உங்கள் தொலைபேசி',
    contactMessagePlaceholder: 'உங்கள் செய்தி...',
    contactSentSuccess: 'உங்கள் செய்தி WhatsApp க்கு அனுப்பப்பட்டது!',
    contactSendAgain: 'மீண்டும் அனுப்பு',
    contactSendViaWa: 'WhatsApp இல் அனுப்பு',
    contactWaIntro: 'ரேபடிகோசம் வலைத்தளத்திலிருந்து செய்தி:',
    contactWaName: 'பெயர்',
    contactWaPhone: 'தொலைபேசி',
    contactWaMessage: 'செய்தி',
    waSuggestTitle: 'எந்த உதவி வேண்டும்?',
    waSuggestSub: 'ஒன்றைத் தேர்ந்தெடுங்கள் — WhatsApp செய்தி தயாராக இருக்கும்',
    waSuggestClose: 'ரத்து',
    waSuggest_subscribe: 'சந்தா எப்படி பெறுவது?',
    waSuggest_subscribe_msg:
      'Hello, I would like details on how to get Repatikosam membership (₹999).',
    waSuggest_payment: 'பணம் செலுத்தினேன் — பத்திரிகை திறக்கவில்லை',
    waSuggest_payment_msg:
      'Hello, I paid but cannot open the full paper. Please help.',
    waSuggest_login: 'உள்நுழைவு பிரச்சனை',
    waSuggest_login_msg: 'Hello, I cannot log in with Google. Please help.',
    waSuggest_paper: 'பத்திரிகை லோட் ஆகவில்லை',
    waSuggest_paper_msg: "Hello, today's paper is not loading for me. Please check.",
    waSuggest_refund: 'பணத்தைத் திரும்ப / ரசீது',
    waSuggest_refund_msg: 'Hello, I need help with a refund or payment receipt.',
    waSuggest_language: 'மொழி எப்படி மாற்றுவது?',
    waSuggest_language_msg:
      'Hello, I need help changing the language on the Repatikosam website.',
    waSuggest_guide: 'தளத்தை எப்படி பயன்படுத்துவது?',
    waSuggest_guide_msg:
      'Hello, can you guide me on how to use the Repatikosam website?',
    waSuggest_other: 'பிற உதவி',
    waSuggest_other_msg: 'Hello, I need help with Repatikosam.',
    headerTaglineFull: 'தெலுங்கு இனத்திற்காக நேர்மையாக · தைரியமாக · தன்னலமின்றி',
    headerEditionLive: 'LIVE',
    headerEditionSealTop: 'ரேபடிகோசம்',
    headerEditionSealBottom: 'தினசரி',
    artScrollHint: '← உருட்டவும் →',
    noStoriesYet: 'கதைகள் விரைவில் இங்கே தோன்றும்.',
    poweredBy: 'இயக்குவது',
    skipToContent: 'உள்ளடக்கத்திற்குச் செல்',
    loadingVideos: 'வீடியோக்கள் ஏற்றப்படுகின்றன…',
  },

  kn: {
    location: '📍 ಅಮರಾವತಿ, ಆಂಧ್ರಪ್ರದೇಶ',
    logout: 'ಲಾಗ್ ಔಟ್',
    signInCopy: 'Google ಮೂಲಕ ಸೈನ್ ಇನ್ ಮಾಡಿ e-paper ಓದಿ, ಸಮೀಕ್ಷೆಯಲ್ಲಿ ಮತ ಹಾಕಿ, ವಿಶೇಷ ಕಥೆಗಳನ್ನು ತೆರೆಯಿರಿ. ಪೂರ್ಣ ಪ್ರವೇಶಕ್ಕೆ ಚಂದಾ ಮಾಡಿ.',
    signInLoading: 'ಸೈನ್ ಇನ್ ಆಗುತ್ತಿದೆ…',
    signInBtn: 'Google ನೊಂದಿಗೆ ಮುಂದುವರಿಸಿ',
    signInEdition: 'ಡಿಜಿಟಲ್ ಆವೃತ್ತಿ · 2026',
    signInLabel: 'ಸದಸ್ಯ ಸೈನ್-ಇನ್',
    signInHeadline: 'ವಾರ್ತಾ ಕೋಣೆಗೆ',
    signInHeadlineEm: 'ಹೆಜ್ಜೆ ಇಡಿ',
    signInMetaPerYear: 'ವರ್ಷಕ್ಕೆ',
    signInMetaEpaper: 'E-paper',
    signInMetaEpaperSub: 'ದೈನಂದಿನ + ಆರ್ಕೈವ್',
    signInMetaSecure: 'ಸುರಕ್ಷಿತ',
    signInMetaSecureSub: 'Google ಮಾತ್ರ',
    signInLegal: 'ಸೈನ್ ಇನ್ ಮಾಡುವುದರಿಂದ ನಮ್ಮ ನಿಯಮಗಳನ್ನು ಒಪ್ಪುತ್ತೀರಿ. Google ನಿಂದ ನಿಮ್ಮ ಹೆಸರು ಮತ್ತು ಇಮೇಲ್ ಮಾತ್ರ ಉಳಿಸುತ್ತೇವೆ.',
    signInTickerTruth: 'ಪ್ರಾಮಾಣಿಕ ಪತ್ರಿಕೋದ್ಯಮ',
    signInTickerEpaper: 'ತೆಲುಗು e-paper',
    signInTickerEditions: 'ದೈನಂದಿನ ಪತ್ರಿಕೆ & ಮಾಸಿಕ ನಿಯತಕಾಲಿಕೆ',
    helpStep2Title: 'Google ಮೂಲಕ ಸೈನ್ ಇನ್',
    paperSliderAria: 'ಪುಟ ಸ್ಲೈಡರ್',
    paperPagesAria: 'ಪುಟಗಳು',
    waSuggestSendHint: 'WhatsApp ತೆರೆಯುತ್ತದೆ — ಸಂದೇಶ ಕಳುಹಿಸಲು ಸಿದ್ಧ',
    testimonialsSub: 'ರೇಪಟಿಕೋಸಂ ಬಗ್ಗೆ ಓದುಗರು ಹೇಳುವುದು',
    searchHowItWorks: 'ಈ ಸೈಟ್ ಅನ್ನು ಹೇಗೆ ಬಳಸುವುದು',
    login: 'ಲಾಗಿನ್',
    signInErrorApiDev: 'API ಚಾಲನೆಯಲ್ಲಿಲ್ಲ. ಇನ್ನೊಂದು ಟರ್ಮಿನಲ್‌ನಲ್ಲಿ: npm run server',
    signInErrorCancelled: 'Google ಸೈನ್ ಇನ್ ರದ್ದಾಗಿದೆ ಅಥವಾ ನಿರ್ಬಂಧಿಸಲಾಗಿದೆ.',
    signInErrorNetwork: 'ಸರ್ವರ್ ತಲುಪಲಾಗುವುದಿಲ್ಲ. ಸಂಪರ್ಕವನ್ನು ಪರಿಶೀಲಿಸಿ.',
    signInErrorFailed: 'Google ಸೈನ್ ಇನ್ ವಿಫಲವಾಯಿತು. ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    launchBadge: '5 Aug launch',
    launchLiveBadge: 'Live',
    launchTitle: '5 August 2026 — Official Repatikosam website launch',
    launchSub: 'Honestly · Fearlessly · Selflessly',
    launchLiveTitle: 'Repatikosam is officially live!',
    launchLiveSub: 'Welcome to the official Repatikosam website',
    launchCta: 'View the paper',
    launchDays: 'Days',
    launchHours: 'Hours',
    launchMins: 'Mins',
    launchSecs: 'Secs',
    launchDismiss: 'Dismiss',
    marketingBadge: 'Annual pass',
    marketingPromoText: 'Daily e-paper + archive — ₹999/year (+ GST)',
    marketingSubscribeBtn: 'ಈಗ ಚಂದಾ ಮಾಡಿ',
    marketingShareBtn: 'Share on WhatsApp',
    marketingShareText: 'Repatikosam Telugu daily — honest news & digital e-paper',
    marketingSectionEyebrow: 'Digital membership',
    marketingSectionTitle: 'Unlock the full edition',
    marketingSectionLead: 'Papers, polls, and exclusive stories — one year of full access.',
    marketingPerk1: 'Daily e-paper & monthly magazine',
    marketingPerk2: 'Past editions archive',
    marketingPerk3: 'Polls & exclusive stories',
    marketingPerk4: 'Read on all devices',
    marketingPerYear: 'year',
    marketingGstNote: '+ 18% GST · Secured by Razorpay',
    marketingSecureNote: 'Instant access after payment',
    marketingFloatSubscribe: 'ಚಂದಾ',
    sidebarSubscribeLead: 'ಪೂರ್ಣ e-paper ಪ್ರವೇಶ',
    previewBtn: 'Try first 2 pages free',
    previewEyebrow: 'Free',
    previewTitle: 'Read the first 2 pages',
    previewSub: 'No subscription needed — try the paper now',
    previewBtnAction: 'Start free read',
    previewLimitBanner: 'Preview ended — subscribe for full edition',
    previewSubscribeCta: 'ಚಂದಾ ಮಾಡಿ ಮುಂದುವರಿಸಿ',
    socialProofTitle: 'Readers trust Repatikosam',
    socialProofReaders: 'Readers',
    socialProofSubs: 'Members',
    socialProofLeads: 'Sample downloads',
    testimonialsEyebrow: 'Reader voices',
    testimonialsTitle: 'What readers say about Repatikosam',
    testimonial1: 'Honest Telugu news and daily e-paper.',
    testimonial1Author: 'Reader, Andhra Pradesh',
    testimonial1Location: 'Andhra Pradesh',
    testimonial2: 'Monthly magazine plus archive in one place.',
    testimonial2Author: 'Reader, Telangana',
    testimonial2Location: 'Telangana',
    testimonial3: 'Reliable Telugu news every morning.',
    testimonial3Author: 'Reader, Guntur',
    testimonial3Location: 'Guntur, Andhra Pradesh',
    testimonial4: 'Great archive access in one place.',
    testimonial4Author: 'Reader, Visakhapatnam',
    testimonial4Location: 'Visakhapatnam, Andhra Pradesh',
    leadTitle: 'Free sample PDF',
    leadLead: 'Enter your email to download the sample booklet.',
    leadEmailPlaceholder: 'you@email.com',
    leadSubmit: 'Get download',
    leadSuccess: 'Thank you! PDF opens in a new tab.',
    leadError: 'Please enter a valid email.',
    faqTitle: 'FAQ',
    faqSubtitle: 'Membership, payments, and e-paper help',
    trustRibbonEyebrow: 'ನಂಬಿಕೆ',
    trustRibbonAria: 'ನಂಬಿಕೆ ಅಳತೆಗಳು',
    trustSince: 'Since 2015',
    trustSinceSub: 'ಮೊದಲ ಬಿಡುಗಡೆ',
    trustReaders: 'ಓದುಗರು',
    trustReadersSub: 'ಬೆಳೆಯುತ್ತಿರುವ ಸಮುದಾಯ',
    trustDaily: 'Daily e-paper',
    trustDailySub: 'ದೈನಂದಿನ ಆವೃತ್ತಿ',
    trustLocation: 'Amaravati · AP',
    todayHeadlinesTitle: "Today's headlines",
    editorsPickTitle: "Editor's pick",
    editorsPickBadge: "Editor's pick",
    searchEyebrow: 'ಹುಡುಕಾಟ',
    searchTitle: 'ಆವೃತ್ತಿಗಳು & ಕಥೆಗಳು',
    searchMinHint: 'ಕನಿಷ್ಠ 2 ಅಕ್ಷರಗಳನ್ನು ಟೈಪ್ ಮಾಡಿ',
    searchClear: 'ಅಳಿಸಿ',
    searchResultsLabel: 'ಫಲಿತಾಂಶಗಳು',
    searchEditions: 'ಆವೃತ್ತಿಗಳು',
    searchStories: 'ಕಥೆಗಳು',
    searchPlaceholder: 'Search headlines…',
    searchNoResults: 'No results',
    guestBarTitle: 'ಪೂರ್ಣ ಪ್ರವೇಶ ಪಡೆಯಿರಿ',
    guestBarText: 'ಇಂದಿನ e-paper, ಆರ್ಕೈವ್ ಮತ್ತು ವಿಶೇಷ ಕಥೆಗಳು',
    guestBarSub: 'ವರ್ಷಕ್ಕೆ ₹999 · GST ಹೆಚ್ಚುವರಿ',
    guestBarPerkPages: 'ಪೂರ್ಣ ಪುಟಗಳು',
    guestBarPerkStory: 'ವಿಶೇಷ ಕಥೆಗಳು',
    guestBarPerkFull: 'ಪೂರ್ಣ ಪ್ರವೇಶ',
    guestBarLater: 'ನಂತರ',
    guestBarBtn: 'ಚಂದಾ ಮಾಡಿ',
    headerSignIn: 'ಸೈನ್ ಇನ್',
    textSizeLabel: 'Text size',
    textSize_sm: 'A',
    textSize_md: 'A+',
    textSize_lg: 'A++',
    editionLiveTitle: "Today's edition is live!",
    editionLiveCta: 'Read the paper',
    shareStoryBtn: 'Share on WhatsApp',
    editionToastTitle: "Today's edition is live!",
    editionToastBody: 'Read today\'s e-paper now.',
    editionToastRead: 'Open paper',
    storyUsefulPrompt: 'Helpful?',
    storyUsefulYes: 'Yes',
    storyUsefulThanks: 'Thanks!',
    relatedStoriesTitle: 'Related',
    freeReadBadge: 'Free',
    subscribersOnlyBadge: 'ಚಂದಾದಾರರಿಗೆ ಮಾತ್ರ',
    categoryNewsTitle: 'By category',
    categoryViewAll: 'View all',
    whatsappChannel: 'WhatsApp Channel',
    youtubeWatchNote: 'Videos + news together',
    faq1q: 'Subscription price?',
    faq1a: '₹999/year + GST. Full digital access.',
    faq2q: 'Access after payment?',
    faq2a: 'Instant after Razorpay payment.',
    faq3q: 'Devices?',
    faq3a: 'Phone, tablet, desktop. Add to home screen.',
    faq4q: 'Referral?',
    faq4a: 'Friends get 2-day trial via your link.',
    faq5q: 'Refund?',
    faq5a: 'Contact contact@repatikosam.com',
    faq6q: 'How to change language?',
    faq6a: 'Tap 🌐 Language at top right — Telugu, English, Tamil, or Kannada.',
    faq7q: "Where is today's paper?",
    faq7a: 'Tap "Today\'s paper" in the bottom bar or the paper preview on the page.',
    faq8q: 'How to make text bigger?',
    faq8a: 'Tap + at the top to make text bigger. Tap − to make it smaller.',
    faq9q: 'How to get help?',
    faq9a: 'Tap WhatsApp at the bottom. Phone: +91 90904 48890',
    langPickerTitle: 'Choose your language',
    langPickerSubtitle: 'உங்கள் மொழியைத் தேர்ந்தெடுக்கவும்',
    langPickerHint: 'You can change language anytime from the top',
    helpGuideTitle: 'ಸೈಟ್ ಮಾರ್ಗದರ್ಶಿ',
    helpGuideSubtitle: '5 simple steps',
    helpGuideOpenFaq: 'See FAQ',
    helpGuideCallWa: 'WhatsApp help',
    helpStep1Title: 'ಭಾಷೆ ಆಯ್ಕೆಮಾಡಿ',
    helpStep1Body: 'Tap 🌐 Language at the top right.',
    helpStep2Body: 'Tap "Today\'s paper" at the bottom.',
    helpStep3Title: 'Bigger text',
    helpStep3Body: 'Tap + at the top to make text bigger.',
    helpStep4Title: 'Sign in',
    helpStep4Body: 'Tap Sign in for full access — Google login.',
    helpStep5Title: 'ಇಂದಿನ ಪತ್ರಿಕೆ ಓದಿ',
    helpStep5Body: 'Tap WhatsApp — our team will help.',
    easyReadingLabel: 'Easy reading',
    easyReadingOn: 'On',
    easyReadingOff: 'Off',
    navHelp: 'ಸಹಾಯ',
    navStories: 'ಕಥೆಗಳು',
    nativeYou: 'ನೀವು',
    nativeToday: 'ಇಂದು',
    nativeGuest: 'ಅತಿಥಿ',
    nativeGuestHint: 'ಪೂರ್ವವೀಕ್ಷಣೆ ನೋಡಿ — ಪೂರ್ಣ ಪತ್ರಿಕೆಗೆ ಸೈನ್ ಇನ್',
    nativeMember: 'ಸದಸ್ಯ',
    nativePrefs: 'ಆದ್ಯತೆಗಳು',
    nativeSupport: 'ಸಹಾಯ',
    nativeLegal: 'ಕಾನೂನು',
    nativeShareApp: 'ಆ್ಯಪ್ ಹಂಚಿಕೊಳ್ಳಿ',
    nativeShare: 'ಹಂಚಿಕೊಳ್ಳಿ',
    nativeEditionAlerts: 'ಹೊಸ ಆವೃತ್ತಿ ಅಲರ್ಟ್',
    nativeOfflineHint: 'ಆಫ್‌ಲೈನ್‌ನಲ್ಲಿ ಓದಿ',
    nativeBookmarks: 'ಉಳಿಸಿದವು',
    nativeBookmark: 'ಉಳಿಸಿ',
    nativeBookmarkSaved: 'ಉಳಿಸಲಾಗಿದೆ',
    nativeSearch: 'ಹುಡುಕಿ',
    nativeSearchPlaceholder: 'ಸುದ್ದಿ ಅಥವಾ ದಿನಾಂಕ…',
    nativeSearchHint: 'ಕನಿಷ್ಠ 2 ಅಕ್ಷರಗಳು',
    nativeSearchEmpty: 'ಫಲಿತಾಂಶಗಳಿಲ್ಲ',
    nativeTheme: 'ಥೀಮ್',
    nativeThemeSystem: 'ಸಿಸ್ಟಮ್',
    nativeThemeLight: 'ಲೈಟ್',
    nativeThemeDark: 'ಡಾರ್ಕ್',
    nativePassActive: 'Active',
    nativePassGet: 'Get',
    nativeUnlockHint: 'ದೈನಂದಿನ ಇ-ಪೇಪರ್, ಆರ್ಕೈವ್, ಕಥೆಗಳನ್ನು ಅನ್‌ಲಾಕ್ ಮಾಡಿ',
    nativeDone: 'ಮುಗಿದಿದೆ',
    nativeMarketsTitle: 'ಮಾರುಕಟ್ಟೆ ದರ',
    nativeMarketsToday: 'ಇಂದು',
    nativeMarketsLive: 'ಇಂದಿನ ದರ',
    nativeMarketGold: 'ಚಿನ್ನ',
    nativeMarketSilver: 'ಬೆಳ್ಳಿ',
    nativeMarketPetrol: 'ಪೆಟ್ರೋಲ್',
    nativeMarketDiesel: 'ಡೀಸೆಲ್',
    nativeMarketTG: 'ತೆಲಂಗಾಣ',
    nativeMarketAP: 'ಆಂಧ್ರ',
    nativeBackToMenu: 'ಮೆನುಗೆ ಹಿಂತಿರುಗಿ',
    nativeBackToApp: 'ಆ್ಯಪ್‌ಗೆ ಹಿಂತಿರುಗಿ',
    nativeValidUntil: 'ಮಾನ್ಯವಾಗುವವರೆಗೆ',
    nativeTrialUntil: 'ಟ್ರಯಲ್ವರೆಗೆ',
    toiTopNews: 'ಟಾಪ್ ನ್ಯೂಸ್',
    navEpaper: 'ಇ-ಪೇಪರ್',
    navVideos: 'ವೀಡಿಯೊ',
    navMenu: 'ಮೆನು',
    textSize_sm_hint: 'Small',
    textSize_md_hint: 'Normal',
    textSize_lg_hint: 'Large',
    referralTitle: 'Invite friends',
    referralFabLabel: 'Invite',
    referralLead: 'Share your link for 2-day friend trial.',
    referralCopy: 'Copy link',
    referralCopied: 'Copied!',
    referralClaimPlaceholder: 'Friend code',
    referralClaimBtn: 'Activate trial',
    referralClaimSuccess: 'Trial activated!',
    referralClaimFail: 'Invalid code.',
    referralShareWa: 'Share on WhatsApp',
    referralShareText: 'Repatikosam Telugu daily',
    referralShareCode: 'Code',
    referralTrialActive: 'Trial until {date}',
    installIosHint: 'Share → Add to Home Screen',
    installIosShort: 'Add to Home Screen',
    installDesktopShort: 'Install the desktop app',
    installSideTab: 'Install',
    installDesktopBtn: 'Install Desktop App',
    tickerLabel: '🔴 ತಾಜಾ',
    headlines: [
      'ಆಂಧ್ರ ಮುಖ್ಯಮಂತ್ರಿ ಚಂದ್ರಬಾಬು ನಾಯ್ಡು ಹೊಸ ಯೋಜನೆಗಳು',
      'ಚಂದ್ರಬಾಬು ನಾಯ್ಡು ಅಭಿವೃದ್ಧಿಪಡಿಸಿದ ರಸ್ತೆಗಳು & ಹೆದ್ದಾರಿಗಳು',
      '2024 ರ ನಂತರ ಭೂಮಿ ಬೆಲೆಗಳು ಗಣನೀಯವಾಗಿ ಏರಿದೆ',
      'ಆಂಧ್ರಪ್ರದೇಶ ರಾಜಕೀಯದಲ್ಲಿ ಹೊಸ ತಿರುವು',
      'ರೇಪಟಿಕೋಸಂ ದಿನಪತ್ರಿಕೆ - ಇಂದಿನ ಮುಖ್ಯಾಂಶಗಳು',
    ],
    todayPaper: '📰 ಇಂದಿನ ಪತ್ರಿಕೆ',
    paperHeadline: 'ದೈನಂದಿನ ಪತ್ರಿಕೆ',
    paperSub: 'ತೆಲುಗು ಜಾತಿಯ ಭವಿಷ್ಯಕ್ಕಾಗಿ ಪ್ರಾಮಾಣಿಕವಾಗಿ...',
    readBtn: 'ಪತ್ರಿಕೆ ಓದಿ',
    readDailyBtn: 'ಇಂದಿನ ಪತ್ರಿಕೆ ಓದಿ',
    readMonthlyBtn: 'ಈ ತಿಂಗಳ ಮ್ಯಾಗಜೀನ್ ಓದಿ',
    browsePastDaily: 'ಹಳೆಯ ದಿನದ ಪತ್ರಿಕೆಗಳು',
    browsePastMonthly: 'ಹಳೆಯ ತಿಂಗಳ ಮ್ಯಾಗಜೀನ್‌ಗಳು',
    pastIssues: 'ಹಳೆಯ ದಿನದ ಪತ್ರಿಕೆಗಳು',
    monthlyCalSub: 'ಯಾವ ತಿಂಗಳ ಮ್ಯಾಗಜೀನ್ ಬೇಕೋ ಆಯ್ಕೆಮಾಡಿ',
    dailyEdition: 'ದೈನಂದಿನ ಪತ್ರಿಕೆ',
    monthlyEdition: 'ಮಾಸಿಕ ಮ್ಯಾಗಜೀನ್‌ಗಳು',
    thisMonth: '📖 ಈ ತಿಂಗಳ ಮ್ಯಾಗಜೀನ್',
    monthlyHeadline: 'ಮಾಸಿಕ ಮ್ಯಾಗಜೀನ್‌ಗಳು',
    categories: [
      { label: 'ರಾಷ್ಟ್ರೀಯ / ಅಂತರರಾಷ್ಟ್ರೀಯ', icon: '🌐' },
      { label: 'ಕ್ರೀಡೆ', icon: '🏏' },
      { label: 'ಮನರಂಜನೆ', icon: '🎬' },
    ],
    viewBtn: 'ನೋಡಿ',
    videosTitle: 'ರೇಪಟಿಕೋಸಂ ವೀಡಿಯೊಗಳು',
    shortsTitle: 'ರೇಪಟಿಕೋಸಂ ಶಾರ್ಟ್ಸ್',
    viewAll: 'ಎಲ್ಲವನ್ನೂ ನೋಡಿ →',
    aboutTitle: 'ರೇಪಟಿಕೋಸಂ ಬಗ್ಗೆ',
    aboutText: "ಸಮಕಾಲೀನ ರಾಜಕೀಯ ಕಲುಷಿತಗೊಂಡು ತೆಲುಗು ಜಾತಿಯ ಭವಿಷ್ಯ ಅತಂತ್ರದಲ್ಲಿದೆ. ರಾಜಕೀಯ ವಿಷ ಆಟದಲ್ಲಿ ನಮ್ಮ ಭಾವಿ ತಲೆಮಾರುಗಳು ಬಲಿಯಾಗದಂತೆ ನೋಡಿಕೊಳ್ಳುವ ಜವಾಬ್ದಾರಿ ಪ್ರತಿಯೊಬ್ಬರ ಮೇಲೂ ಇದೆ. ರಾಜಕೀಯ ಕನ್ನಡಕದ ಮೂಲಕ ಅಲ್ಲದೆ ವಾಸ್ತವಿಕ ದೃಷ್ಟಿಕೋನದಿಂದ ಜನರಿಗೆ ಅರಿವು ಮೂಡಿಸಲು 'ರೇಪಟಿಕೋಸಂ' ದಿನಪತ್ರಿಕೆ, ಮಾಸಿಕ ಮ್ಯಾಗಜೀನ್ ಪ್ರಾರಂಭಿಸಲಾಯಿತು.",
    adsTitle: 'ಜಾಹೀರಾತುಗಳು',
    adSponsored: 'ಪ್ರಾಯೋಜಿತ',
    adPartnerStrip: 'ನಮ್ಮ ಪಾಲುದಾರರು',
    home: 'ಮುಖಪುಟ',
    about: 'ನಮ್ಮ ಬಗ್ಗೆ',
    contact: 'ಸಂಪರ್ಕಿಸಿ',
    rights: 'ಎಲ್ಲ ಹಕ್ಕುಗಳನ್ನು ಕಾಯ್ದಿರಿಸಲಾಗಿದೆ.',
    trustStrip: 'ಸಾರ್ವಜನಿಕ ಹಿತ ಪತ್ರಿಕೋದ್ಯಮ • ಸತ್ಯಾಧಾರಿತ ಸಂಪಾದಕೀಯ • ಜವಾಬ್ದಾರಿಯುತ ಪ್ರಕಟಣೆ',
    footerMadeInAp: 'ಅಮರಾವತಿ, ಆಂಧ್ರಪ್ರದೇಶ ಆಧಾರಿತ ಸಂಪಾದಕೀಯ ಡಿಜಿಟಲ್ ವೇದಿಕೆ.',
    nav: ['ಮುಖಪುಟ', 'ಈ ಪತ್ರಿಕೆ', 'ಹಳೆಯ ಪತ್ರಿಕೆಗಳು', 'ಅಭಿಪ್ರಾಯ'],
    navMore: 'ಇನ್ನಷ್ಟು',
    navWhatsApp: 'WhatsApp',
    calTitle: 'ಹಳೆಯ ದಿನದ ಪತ್ರಿಕೆಗಳು',
    calSub: 'ಯಾವ ದಿನದ ಪತ್ರಿಕೆ ಬೇಕೋ ಆಯ್ಕೆಮಾಡಿ',
    calRecent: 'ಇತ್ತೀಚಿನ ಪತ್ರಿಕೆಗಳು',
    calAvailable: 'ಪತ್ರಿಕೆ ಲಭ್ಯವಿದೆ',
    calToday: 'ಇಂದು',
    months: ['ಜನವರಿ','ಫೆಬ್ರವರಿ','ಮಾರ್ಚ್','ಏಪ್ರಿಲ್','ಮೇ','ಜೂನ್','ಜುಲೈ','ಆಗಸ್ಟ್','ಸೆಪ್ಟೆಂಬರ್','ಅಕ್ಟೋಬರ್','ನವೆಂಬರ್','ಡಿಸೆಂಬರ್'],
    archiveTitle: 'E-ಪೇಪರ್ ಸಂಗ್ರಹ',
    archiveIssues: 'ಸಂಚಿಕೆಗಳು',
    archiveEmpty: 'ಇನ್ನೂ ಪತ್ರಿಕೆಗಳು ಅಪ್ಲೋಡ್ ಆಗಿಲ್ಲ',
    pollTitle: 'ನಿಮ್ಮ ಅಭಿಪ್ರಾಯ',
    pollHeadHint: 'ಒಂದು ಆಯ್ಕೆ ಮಾಡಿ',
    pollVotes: 'ಮತಗಳು',
    pollThanks: '✅ ನಿಮ್ಮ ಮತ ದಾಖಲಾಗಿದೆ!',
    statEditions: 'ಸಂಚಿಕೆಗಳು',
    statEst: 'ಸ್ಥಾಪಿಸಲಾಯಿತು',
    statLang: 'ತೆಲುಗು',
    statLangLabel: 'ಭಾಷೆ',
    statState: 'ಆಂಧ್ರಪ್ರದೇಶ',
    statStateLabel: 'ರಾಜ್ಯ',
    latestNews: 'ಇತ್ತೀಚಿನ ಸಂಗತಿಗಳು',
    articlesTitle: 'ವಿಶೇಷ ಕಥೆಗಳು',
    readMore: 'ಮತ್ತಷ್ಟು ಓದಿ',
    ytDesc: 'ತೆಲುಗು ಜಾತಿಯ ಭವಿಷ್ಯಕ್ಕಾಗಿ ಪ್ರಾಮಾಣಿಕವಾಗಿ...',
    ytSubscribe: 'ಸಬ್ಸ್ಕ್ರೈಬ್ ಮಾಡಿ',
    ytVideos: 'ವೀಡಿಯೋಗಳು',
    ytShorts: 'ಶಾರ್ಟ್ಸ್',
    ytFeatured: 'ಇತ್ತೀಚಿನ ವೀಡಿಯೊ',
    ytTitle: 'ರೇಪಟಿಕೋಸಂ YouTube',
    ytNowPlaying: 'ಪ್ಲೇ ಆಗುತ್ತಿದೆ',
    ytPlaylist: 'ವೀಡಿಯೊಗಳು',
    zonesLabel: 'AP ಪ್ರಕಟಣಾ ಕೇಂದ್ರಗಳು',
    zonesLead: 'ಕರಾವಳಿಯ ಆಂಧ್ರಪ್ರದೇಶದಿಂದ ರಾಯಲಸೀಮ ವರೆಗೆ — ಒಂದು ಪತ್ರಿಕೆ, ಆರು ಪ್ರಕಟಣಾ ಕೇಂದ್ರಗಳು.',
    zones: [
      { icon: '🕰️', name: 'ಗುಂಟೂರು' },
      { icon: '🌉', name: 'ವಿಜಯವಾಡ' },
      { icon: '🗼', name: 'ಶ್ರೀಕಾಕುಳಂ' },
      { icon: '🌊', name: 'ವಿಶಾಖಪಟ್ಟಣಮ್' },
      { icon: '🌴', name: 'ರಾಜಮಹೇಂದ್ರವರಮ್' },
      { icon: '🏰', name: 'ಕಡಪ' },
    ],
    brandName: 'ರೇಪಟಿಕೋಸಂ',
    legalEntityName: 'M S Publications',
    legalEntityLabel: 'ನೋಂದಾಯಿತ ವ್ಯವಹಾರ',
    tradeNameLabel: 'ವ್ಯಾಪಾರ ಹೆಸರು',
    gstLabel: 'GSTIN',
    businessTypeLabel: 'ವ್ಯವಹಾರ ಪ್ರಕಾರ',
    youtube: 'YouTube',
    facebook: 'Facebook',
    instagram: 'Instagram',
    mobileAppEyebrow: 'ಮೊಬೈಲ್ ಆ್ಯಪ್',
    mobileAppTitle: 'Android & iOS — ಶೀಘ್ರದಲ್ಲೇ',
    mobileAppSub: '1–2 ವಾರಗಳಲ್ಲಿ Google Play ಮತ್ತು App Store ನಲ್ಲಿ',
    mobileAppPlay: 'Google Play',
    mobileAppIos: 'App Store',
    mobileAppSoon: 'ಶೀಘ್ರದಲ್ಲೇ',
    footerQuickLinks: 'ತ್ವರಿತ ಲಿಂಕ್‌ಗಳು',
    footerCompany: 'ಸಂಸ್ಥೆ',
    footerLegal: 'ಕಾನೂನು',
    footerContact: 'ಸಂಪರ್ಕ',
    footerTerms: 'ನಿಯಮಗಳು',
    footerPrivacy: 'ಗೌಪ್ಯತೆ ನೀತಿ',
    footerRefund: 'ಮರುಪಾವತಿ ನೀತಿ',
    footerCancellation: 'ರದ್ದತಿ ನೀತಿ',
    legalBackHome: '← ಮುಖಪುಟ',
    legalLangEn: 'English',
    legalLangTe: 'తెలుగు',
    legalTeNote: 'Full legal text is in English.',
    legalPoliciesNav: 'ಕಾನೂನು ನೀತಿಗಳು',
    legalHeroSub: 'Official policies for Repatikosam, operated by M S Publications.',
    contactPageHeroSub: 'ನಾವು ಸಹಾಯಕ್ಕೆ ಸಿದ್ಧರಿದ್ದೇವೆ',
    contactFormSub: 'ಸಂದೇಶ ಕಳುಹಿಸಿ — WhatsApp ತೆರೆಯುತ್ತದೆ',
    cookieConsentTitle: 'Cookie preferences',
    cookieConsentText: 'We use analytics cookies to improve the site. Accept or reject optional cookies.',
    cookieConsentAccept: 'Accept',
    cookieConsentReject: 'Reject',
    editionNotifyPrompt: 'Notify when a new edition is live?',
    editionNotifyEnable: 'Allow',
    editionNotifyLater: 'Later',
    editionNotifyTitle: 'Repatikosam — new edition',
    editionNotifyBody: "Today's e-paper is available.",
    subscribeBillingNotice: 'ಒಂದು ಬಾರಿ ವಾರ್ಷಿಕ ಪಾವತಿ · ಸ್ವಯಂ ಡೆಬಿಟ್ ಇಲ್ಲ',
    paymentSuccessTitle: 'Payment successful!',
    paymentSuccessBody: 'Subscription active. Read the e-paper now.',
    paymentSuccessUntil: 'Access until {date}.',
    paymentSuccessReceipt: 'Receipt link below.',
    paymentSuccessEmailSent: 'Receipt emailed to {email}.',
    paymentSuccessReceiptLink: 'View billing receipt',
    paymentSuccessCta: 'Read paper →',
    subProcessingTitle: 'Processing payment…',
    subProcessingText: 'Unlocking your access…',
    subUnlockHeadline: 'Unlock the full edition',
    subUnlockLead: 'Annual membership tied to your Google account — daily e-paper, archives, and every story.',
    subBullet1: 'Daily digital newspaper',
    subBullet2: 'Monthly magazine archive',
    subBullet3: 'All devices, one account',
    subBullet4: '365 days of access',
    subModalTitle: 'Subscribe to continue',
    subModalSub: 'Unlock the full e-paper & archive',
    subSignedInAs: 'Signed in as',
    subSwitchAccount: 'Use a different account',
    subAnnualPass: 'Annual pass',
    subPerYear: '/ year',
    subLinkedGmail: 'Subscription is linked to this Gmail only.',
    subPlanPrice: 'Plan price',
    subGstLabel: 'GST (18%)',
    subTotalDue: 'Total due',
    subPleaseWait: 'Please wait…',
    subPayUnlock: 'Pay ₹{total} & unlock',
    subInstantAccess: 'Instant access after successful payment',
    subWhatsappLabel: 'WhatsApp number',
    subWhatsappPlaceholder: '9876543210',
    subWhatsappOptIn: 'Send me a WhatsApp when today’s paper is live',
    subWhatsappRequired: 'Enter a valid 10-digit WhatsApp number',
    subWhatsappOptInRequired: 'Please allow WhatsApp alerts for today’s paper',
    subSecuredRazorpay: 'Secured by Razorpay · UPI, cards, netbanking',
    subDemoMode: 'Demo mode — add Razorpay keys on the server to accept real payments.',
    subTestModeTitle: 'Test payment mode',
    subTestModeUpiNote: 'The UPI QR will show invalid if you scan with real GPay / PhonePe — that is normal in test mode.',
    subTestModeUpiItem: 'UPI: pay with success@razorpay',
    subTestModeCardItem: 'Card: 4111 1111 1111 1111, any future expiry, any CVV',
    subTestModeFoot: 'For real QR payments, switch to Live keys in Razorpay after KYC.',
    subPaymentCancelled: 'Payment cancelled',
    subPaymentError: 'Payment could not be completed. Please try again.',
    subServerError: 'Cannot reach the server. Check your connection and try again.',
    subInvalidPayment: 'Invalid payment response from server.',
    subVerifyFailed: 'Payment verification failed',
    subOrderError: 'Could not start payment. Please try again.',
    feedErrorMsg: 'ಕಥೆಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗಲಿಲ್ಲ. ಸಂಪರ್ಕವನ್ನು ಪರಿಶೀಲಿಸಿ.',
    feedErrorRetry: 'Try again',
    footerLocation: 'ಅಮರಾವತಿ, ಆಂಧ್ರಪ್ರದೇಶ',
    insideLabel: 'ಈ ಸಂಚಿಕೆಯಲ್ಲಿ',
    insidePolitics: 'ರಾಜಕೀಯ',
    insideAgri: 'ಕೃಷಿ',
    insideEdu: 'ಶಿಕ್ಷಣ',
    insideNational: 'ರಾಷ್ಟ್ರೀಯ',
    insideIntl: 'ಅಂತರರಾಷ್ಟ್ರೀಯ',
    insideSports: 'ಕ್ರೀಡೆ',
    yearsLabel: 'ವರ್ಷಗಳು',
    issuesLabel: 'ಸಂಚಿಕೆಗಳು',
    readersLabel: 'ಓದುಗರು',
    loading: 'ಲೋಡ್ ಆಗುತ್ತಿದೆ...',
    paperNotAvailable: 'ಈ ದಿನಾಂಕಕ್ಕೆ ಪತ್ರಿಕೆ ಲಭ್ಯವಿಲ್ಲ',
    serverError: 'ಸರ್ವರ್ ದೋಷ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    closeBtn: 'ಮುಚ್ಚಿ',
    calDays: ['ಭಾ', 'ಸೋ', 'ಮಂ', 'ಬು', 'ಗು', 'ಶು', 'ಶ'],
    catAll: 'ಎಲ್ಲಾ',
    catPolitics: 'ರಾಜಕೀಯ',
    catNews: 'ಸುದ್ದಿ',
    catTurmeric: 'ಅರಿಶಿನ',
    listenBtn: 'ಕೇಳಿ',
    stopListen: 'ನಿಲ್ಲಿಸಿ',
    readAloudTitle: 'ಜೋರಾಗಿ ಓದಿ',
    zonesCountSuffix: 'ಕೇಂದ್ರಗಳು',
    pollLangTip: 'ಮತದಾನ ಪಠ್ಯ ತೆಲುಗಿನಲ್ಲಿ',
    sharePaperBadge: 'ಪತ್ರಿಕೆ',
    shareWhatsAppTitle: 'WhatsApp ನಲ್ಲಿ ಹಂಚಿಕೊಳ್ಳಿ',
    paperViewerTitle: 'ರೇಪಟಿಕೋಸಂ ಪತ್ರಿಕೆ',
    shareMessage: 'ರೇಪಟಿಕೋಸಂ ದಿನಪತ್ರಿಕೆ',
    paperScrollHint: 'ಪುಟಗಳು — ಕೆಳಗೆ ಸ್ಕ್ರಾಲ್ ಮಾಡಿ',
    paperPrev: 'ಹಿಂದಿನ',
    paperNext: 'ಮುಂದೆ',
    paperPageOf: 'ಪುಟ',
    paperViewScroll: 'ಸ್ಕ್ರಾಲ್',
    paperViewPages: 'ಪುಟಗಳು',
    paperScrollAllHint: 'ಎಲ್ಲಾ ಪುಟಗಳಿಗಾಗಿ ಕೆಳಗೆ ಸ್ಕ್ರಾಲ್ ಮಾಡಿ',
    paperZoomLabel: 'ಝೂಮ್',
    paperZoomIn: 'ಝೂಮ್ ಇನ್',
    paperZoomOut: 'ಝೂಮ್ ಔಟ್',
    paperZoomReset: 'ರೀಸೆಟ್',
    paperRetry: 'ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ',
    paperSwipeHint: 'ಪುಟ ಬದಲಾಯಿಸಲು ಎಡ/ಬಲ ಸ್ವೈಪ್ ಮಾಡಿ',
    paperPinchHint: 'ಜೂಮ್ ಮಾಡಲು ಎರಡು ಬೆರಳುಗಳಿಂದ ಪಿಂಚ್ ಮಾಡಿ',
    installTitle: 'ರೇಪಟಿಕೋಸಂ',
    installSub: 'ಆ್ಯಪ್ ಆಗಿ ಸ್ಥಾಪಿಸಿ',
    installBtn: 'ಸ್ಥಾಪಿಸಿ',
    dismissBtn: 'ವಜಾಗೊಳಿಸಿ',
    aboutHeroTagline: 'ತೆಲುಗು ಜಾತಿಯ ಭವಿಷ್ಯಕ್ಕಾಗಿ ಪ್ರಾಮಾಣಿಕವಾಗಿ...',
    aboutSectionHeading: 'ನಮ್ಮ ಬಗ್ಗೆ',
    roleManagingDirector: 'Managing Director',
    roleChiefEditor: 'Chief Editor',
    aboutLeadershipHeading: 'ನಮ್ಮ ನಾಯಕತ್ವ',
    aboutStatMonthly: 'ಮಾಸಿಕ ಮ್ಯಾಗಜೀನ್',
    aboutStatDaily: 'ದಿನಪತ್ರಿಕೆ',
    aboutStatState: 'ಆಂಧ್ರಪ್ರದೇಶ',
    aboutStatStateLabel: 'ರಾಜ್ಯ',
    aboutSocialMedia: 'ಸಾಮಾಜಿಕ ಮಾಧ್ಯಮ',
    contactAddressLabel: 'ವಿಳಾಸ',
    contactAddress: 'ಅಮರಾವತಿ, ಆಂಧ್ರಪ್ರದೇಶ, ಭಾರತ',
    contactEmailLabel: 'ಇಮೇಲ್',
    contactFormTitle: 'ಸಂದೇಶ ಕಳುಹಿಸಿ',
    contactNameLabel: 'ಹೆಸರು',
    contactPhoneLabel: 'ಫೋನ್ ಸಂಖ್ಯೆ',
    contactWhatsAppLabel: 'WhatsApp',
    contactReachUs: 'ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ',
    contactBusinessTitle: 'ವ್ಯವಹಾರ ವಿವರಗಳು',
    contactIntlNoteLabel: 'ಅಂತರರಾಷ್ಟ್ರೀಯ ಗ್ರಾಹಕರು',
    contactIntlNote:
      'We accept payments from India and international customers via Razorpay (INR). Your bank may apply currency conversion fees on international cards.',
    contactMessageLabel: 'ಸಂದೇಶ',
    contactNamePlaceholder: 'ನಿಮ್ಮ ಹೆಸರು',
    contactPhonePlaceholder: 'ನಿಮ್ಮ ಫೋನ್ ಸಂಖ್ಯೆ',
    contactMessagePlaceholder: 'ನಿಮ್ಮ ಸಂದೇಶ...',
    contactSentSuccess: 'ನಿಮ್ಮ ಸಂದೇಶ WhatsApp ಗೆ ಕಳುಹಿಸಲಾಗಿದೆ!',
    contactSendAgain: 'ಮತ್ತೆ ಕಳುಹಿಸಿ',
    contactSendViaWa: 'WhatsApp ನಲ್ಲಿ ಕಳುಹಿಸಿ',
    contactWaIntro: 'ರೇಪಟಿಕೋಸಂ ವೆಬ್‌ಸೈಟ್‌ನಿಂದ ಸಂದೇಶ:',
    contactWaName: 'ಹೆಸರು',
    contactWaPhone: 'ಫೋನ್',
    contactWaMessage: 'ಸಂದೇಶ',
    waSuggestTitle: 'ಯಾವ ಸಹಾಯ ಬೇಕು?',
    waSuggestSub: 'ಒಂದನ್ನು ಆರಿಸಿ — WhatsApp ಸಂದೇಶ ಸಿದ್ಧವಾಗಿರುತ್ತದೆ',
    waSuggestClose: 'ರದ್ದು',
    waSuggest_subscribe: 'ಸದಸ್ಯತ್ವ ಹೇಗೆ ಪಡೆಯುವುದು?',
    waSuggest_subscribe_msg:
      'Hello, I would like details on how to get Repatikosam membership (₹999).',
    waSuggest_payment: 'ಪಾವತಿ ಮಾಡಿದೆ — ಪತ್ರಿಕೆ ತೆರೆಯುತ್ತಿಲ್ಲ',
    waSuggest_payment_msg:
      'Hello, I paid but cannot open the full paper. Please help.',
    waSuggest_login: 'ಲಾಗಿನ್ ಸಮಸ್ಯೆ',
    waSuggest_login_msg: 'Hello, I cannot log in with Google. Please help.',
    waSuggest_paper: 'ಪತ್ರಿಕೆ ಲೋಡ್ ಆಗುತ್ತಿಲ್ಲ',
    waSuggest_paper_msg: "Hello, today's paper is not loading for me. Please check.",
    waSuggest_refund: 'ಮರುಪಾವತಿ / ರಸೀದಿ',
    waSuggest_refund_msg: 'Hello, I need help with a refund or payment receipt.',
    waSuggest_language: 'ಭಾಷೆ ಹೇಗೆ ಬದಲಾಯಿಸುವುದು?',
    waSuggest_language_msg:
      'Hello, I need help changing the language on the Repatikosam website.',
    waSuggest_guide: 'ಸೈಟ್ ಅನ್ನು ಹೇಗೆ ಬಳಸುವುದು?',
    waSuggest_guide_msg:
      'Hello, can you guide me on how to use the Repatikosam website?',
    waSuggest_other: 'ಇತರ ಸಹಾಯ',
    waSuggest_other_msg: 'Hello, I need help with Repatikosam.',
    headerTaglineFull: 'ತೆಲುಗು ಜಾತಿಗಾಗಿ ಪ್ರಾಮಾಣಿಕವಾಗಿ · ನಿರ್ಭಯವಾಗಿ · ನಿಸ್ವಾರ್ಥವಾಗಿ',
    headerEditionLive: 'LIVE',
    headerEditionSealTop: 'ರೇಪಟಿಕೋಸಂ',
    headerEditionSealBottom: 'ದೈನಂದಿನ',
    artScrollHint: '← ಸ್ಕ್ರಾಲ್ →',
    noStoriesYet: 'ಕಥೆಗಳು ಶೀಘ್ರದಲ್ಲೇ ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತವೆ.',
    poweredBy: 'ಪವರ್ಡ್ ಬೈ',
    skipToContent: 'ವಿಷಯಕ್ಕೆ ಹೋಗಿ',
    loadingVideos: 'ವೀಡಿಯೊಗಳು ಲೋಡ್ ಆಗುತ್ತಿವೆ…',
  },
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(readStoredLang);

  const switchLang = (l) => {
    const next = SUPPORTED_LANGS.includes(l) ? l : DEFAULT_LANG;
    setLang(next);
    markLangPicked();
    try {
      localStorage.setItem('rk_lang', next);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const dict = translations[lang] || translations[DEFAULT_LANG];
  const t = new Proxy(dict, {
    get(target, prop) {
      if (typeof prop === 'symbol') return target[prop];
      const v = target[prop];
      if (v != null && v !== '') return v;
      const en = translations.en?.[prop];
      if (en != null && en !== '') return en;
      return translations[DEFAULT_LANG]?.[prop];
    },
  });

  return (
    <LanguageContext.Provider value={{ lang, switchLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
